import { useEffect, useMemo, useRef, useState } from "react";
import {
  clearCache,
  createThicknessBottle,
  deleteThicknessMultipleAnalysis,
  fetchThicknessBottleAnalysisStates,
  fetchThicknessBottleHeavy,
  fetchThicknessData,
  fetchThicknessIndex,
  fetchThicknessMetadata,
  fetchThicknessMultipleAnalysisHeavy,
  loadThicknessMultipleAnalyses,
  markThicknessBottleRecalcFailed,
  saveThicknessMultipleAnalysis,
  updateThicknessMultipleAnalysis,
  updateThicknessBottle
} from "../api/postgrest";
import ThicknessFilterPanel from "../components/ThicknessFilterPanel";
import ThicknessTable, { ThicknessSortState } from "../components/ThicknessTable";
import ThicknessGallery from "../components/ThicknessGallery";
import ThicknessDetailModal from "../components/ThicknessDetailModal";
import NasLoginModal from "../components/NasLoginModal";
import ConfirmDialog from "../components/ConfirmDialog";
import PlotBundlePanel from "../components/thickness/PlotBundlePanel";
import { useEscapeStack } from "../hooks/useEscapeStack";
import { useNas } from "../context/NasContext";
import {
  ThicknessAnalysisContract,
  ThicknessAnalysisPayload,
  ThicknessBottle,
  ThicknessBottlePayload,
  ThicknessMeanProfilePoint,
  ThicknessDataPoint,
  SavedThicknessMultipleAnalysis,
  ThicknessMultipleAnalysisPayload,
  ThicknessRecalcStatus,
  TrialType
} from "../types";
import {
  ThicknessFilters,
  applyThicknessFilters,
  defaultThicknessFilters
} from "../utils/thicknessFiltering";
import { toAppFileUrl } from "../utils/fileUrls";
import { formatAppDateTime, formatCurrentDateTime } from "../utils/dateTime";
import {
  getAnalysisContractForViewport,
  hasPlotBundle,
  viewportClassFromWidth
} from "../utils/thicknessPlotBundle";
import { isShowMode } from "../showMode";
import { createZipBlob, type ZipEntryInput } from "../utils/zip";

const PAGE_SIZE = 12;
const NAS_THICKNESS_DIR = "thickness_plotter";
const NAS_THICKNESS_DRY_RUN_DIR = "thickness_plotter/_dry_run";
const NAS_THICKNESS_MULTIPLE_DIR = "thickness_plotter/multiple_analyses";
const THICKNESS_ANALYSIS_STUB = "average_bottle";
const THICKNESS_ANALYSIS_SCHEMA_VERSION = "thickness_analysis.v2";
const THICKNESS_OUTPUT_SUFFIXES = [
  "_thickness_plots.html",
  "_thickness_heatmap.png",
  "_slices_profile.png",
  "_slices_profile_relative.png"
];
const SYNC_PAUSE_POLL_MS = 250;
const SHOW_FILTER_SELECTION_PATH = "./db/filter_selection.json";

type LogTone = "info" | "success" | "warn" | "error";

type LogEntry = {
  message: string;
  tone: LogTone;
};

type SyncProgressState = {
  foldersTotal: number;
  foldersDone: number;
  currentFolder: string;
  filesTotal: number;
  filesDone: number;
  totalFiles: number;
  totalFilesDone: number;
};

type PendingRecalcPrompt = {
  kind: "schema" | "raw-data";
  title: string;
  message: string;
  bottleIds: number[];
  totalSelected?: number;
  schemaById?: Record<number, string | null>;
  failedBottleIds?: number[];
  recalcStatusById?: Record<number, ThicknessRecalcStatus>;
  remainingBottleIds?: number[];
};

type AnalysisPhase = "idle" | "preflight" | "running";

type AnalysisProgressState = {
  label: string;
  percent: number;
  detail?: string;
};

type RecalcChoice = "recalc" | "exclude";

type ShowFilterSelection = {
  thickness_analysis_ids?: unknown[];
};

const buildInitialSyncProgress = (): SyncProgressState => ({
  foldersTotal: 0,
  foldersDone: 0,
  currentFolder: "",
  filesTotal: 0,
  filesDone: 0,
  totalFiles: 0,
  totalFilesDone: 0
});

const LOG_TONE_COLORS: Record<LogTone, string> = {
  info: "var(--text-muted)",
  success: "var(--status-ok)",
  warn: "#f7d774",
  error: "var(--status-error)"
};

function joinNasPath(...parts: Array<string | undefined>) {
  const cleaned = parts
    .filter(Boolean)
    .map((part, index) => {
      const value = String(part);
      if (index === 0) return value.replace(/[\\/]+$/, "");
      return value.replace(/^[\\/]+|[\\/]+$/g, "");
    });
  return cleaned.join("/");
}

const KNOWN_TRIAL_TYPES = ["Glaschemie", "Brennstoff", "Energieverhältnis", "Formgebung"];

function createEmptyTrialSelections(): Record<TrialType, string[]> {
  return {
    Glaschemie: [],
    Brennstoff: [],
    "Energieverhältnis": [],
    Formgebung: []
  };
}

function datePart(value: string | null | undefined) {
  const text = String(value || "").trim();
  return text ? text.slice(0, 10) : "";
}

function describeDateRange(values: Array<string | null | undefined>) {
  const unique = Array.from(
    new Set(values.map((value) => datePart(value)).filter(Boolean))
  ).sort();
  if (unique.length === 0) return "All";
  if (unique.length === 1) return unique[0];
  return `${unique[0]} to ${unique[unique.length - 1]}`;
}

function collectTrialSelections(rows: ThicknessBottle[]): Record<TrialType, string[]> {
  const map = new Map<string, Set<string>>();
  for (const row of rows) {
    for (const trial of row.active_trials || []) {
      if (!KNOWN_TRIAL_TYPES.includes(trial.trial_type)) continue;
      const set = map.get(trial.trial_type) || new Set<string>();
      set.add(trial.descriptive_name);
      map.set(trial.trial_type, set);
    }
  }
  const selections = createEmptyTrialSelections();
  for (const [trialType, values] of map) {
    selections[trialType] = Array.from(values).sort();
  }
  return selections;
}

function buildShowThicknessFilters(rows: ThicknessBottle[]): ThicknessFilters {
  return {
    date: null,
    trials: collectTrialSelections(rows)
  };
}

function buildShowThicknessFilterLabels(rows: ThicknessBottle[]) {
  return {
    date: describeDateRange(rows.map((row) => row.timestamp))
  };
}

const sanitizeFolderSegment = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 48);

const buildMultipleAnalysisFolderName = (id: number, name: string) => {
  const slug = sanitizeFolderSegment(name);
  return slug ? `${id}_${slug}` : String(id);
};

const deriveTmpRoot = (outputDir: string) => {
  const cleaned = outputDir.replace(/\\/g, "/").replace(/\/$/, "");
  const parts = cleaned.split("/");
  if (parts[parts.length - 1] === "output") {
    return parts.slice(0, -1).join("/");
  }
  return cleaned;
};

const parseSliceCount = (content: string) => {
  const match = /number\s*of\s*slices\s*:\s*(\d+)/i.exec(content);
  if (!match) return null;
  const value = Number(match[1]);
  return Number.isFinite(value) ? value : null;
};

const timestampComparable = (value: string | null | undefined): string | null => {
  if (!value || typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  const isoLike = /(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2})/.exec(trimmed);
  if (isoLike) {
    return `${isoLike[1]}T${isoLike[2]}`;
  }

  const filenameLike = /(\d{4}-\d{2}-\d{2})_(\d{2}-\d{2}-\d{2})/.exec(trimmed);
  if (filenameLike) {
    return `${filenameLike[1]}T${filenameLike[2].replace(/-/g, ":")}`;
  }

  const parsed = Date.parse(trimmed);
  if (Number.isFinite(parsed)) {
    return new Date(parsed).toISOString().slice(0, 19);
  }
  return null;
};

const timestampFromCsvFilename = (filename: string): string | null => {
  const match = /_(\d{4}-\d{2}-\d{2})_(\d{2}-\d{2}-\d{2})(?:_|$)/.exec(filename);
  if (!match) return null;
  return `${match[1]}T${match[2].replace(/-/g, ":")}`;
};

const hashFingerprint = (raw: string): string => {
  let hash = 5381;
  for (let index = 0; index < raw.length; index += 1) {
    hash = ((hash << 5) + hash) ^ raw.charCodeAt(index);
  }
  return (hash >>> 0).toString(16);
};

type NasEntryMeta = {
  name: string;
  isFile: boolean;
  isDir: boolean;
  sizeBytes?: number;
  mtimeMs?: number;
};

const buildQuickBottleFingerprint = (entries: NasEntryMeta[], bottleId: number): string | null => {
  const rows = entries
    .filter((entry) => entry.isFile && entry.name.toLowerCase().endsWith(".csv"))
    .map((entry) => {
      const match = /^bottle[^0-9]*([0-9]+).*slice[^0-9]*([0-9]+)\.csv$/i.exec(entry.name);
      if (!match) return null;
      const parsedBottleId = Number(match[1]);
      const parsedSliceId = Number(match[2]);
      if (!Number.isFinite(parsedBottleId) || parsedBottleId !== bottleId || !Number.isFinite(parsedSliceId)) {
        return null;
      }
      const sizeBytes = Number.isFinite(Number(entry.sizeBytes)) ? Number(entry.sizeBytes) : -1;
      const mtimeMs = Number.isFinite(Number(entry.mtimeMs)) ? Math.round(Number(entry.mtimeMs)) : -1;
      return {
        sliceId: parsedSliceId,
        name: entry.name,
        sizeBytes,
        mtimeMs
      };
    })
    .filter(
      (row): row is { sliceId: number; name: string; sizeBytes: number; mtimeMs: number } => !!row
    )
    .sort((a, b) => a.sliceId - b.sliceId || a.name.localeCompare(b.name));

  if (!rows.length) return null;
  const encoded = rows
    .map((row) => `${row.sliceId}:${row.name}:${row.sizeBytes}:${row.mtimeMs}`)
    .join("|");
  return `qf_v1_${hashFingerprint(`${bottleId}|${rows.length}|${encoded}`)}`;
};

const getAnalysisSchemaVersion = (
  payload: ThicknessAnalysisContract | null | undefined
): string | null => {
  if (!payload || typeof payload !== "object") return null;
  if (typeof payload.schema_version === "string") return payload.schema_version;
  if (typeof payload.analyzer_meta?.schema_version === "string") {
    return payload.analyzer_meta.schema_version;
  }
  return null;
};

const isBottleRecalcCurrent = (bottle: ThicknessBottle | null | undefined): boolean => {
  if (!bottle) return false;
  const payload =
    bottle.analysis_payload && typeof bottle.analysis_payload === "object"
      ? (bottle.analysis_payload as ThicknessAnalysisContract)
      : null;
  if (!payload) return false;
  if (getAnalysisSchemaVersion(payload) !== THICKNESS_ANALYSIS_SCHEMA_VERSION) return false;
  const meanProfile = canonicalizeMeanProfile(
    bottle.mean_thickness_profile ?? payload.mean_thickness_profile
  );
  return meanProfile.length > 1;
};

const formatBottleIdSample = (ids: number[], limit = 8) => {
  const sample = ids.slice(0, limit).join(", ");
  return sample ? `${sample}${ids.length > limit ? ", ..." : ""}` : "";
};

const buildOutdatedSchemaMessage = (
  outdatedBottleIds: number[],
  totalCount: number,
  schemaById: Record<number, string | null>
) => {
  const detectedSchemas = Array.from(
    new Set(
      outdatedBottleIds
        .map((id) => schemaById[id] ?? "missing")
        .filter((value): value is string => typeof value === "string" && value.length > 0)
    )
  );
  const detectedHint = detectedSchemas.length
    ? ` Detected schema(s): ${detectedSchemas.join(", ")}.`
    : "";
  const sample = formatBottleIdSample(outdatedBottleIds);
  const idHint = sample ? ` Blocked IDs: ${sample}.` : "";
  return (
    `${outdatedBottleIds.length}/${totalCount} selected bottle(s) cannot be used in the current thickness analysis (${THICKNESS_ANALYSIS_SCHEMA_VERSION}).` +
    detectedHint +
    idHint
  );
};

const getDetectedSchemaLabels = (
  bottleIds: number[],
  schemaById: Record<number, string | null> | null | undefined
) =>
  Array.from(
    new Set(
      bottleIds
        .map((id) => schemaById?.[id] ?? "missing")
        .filter((value): value is string => typeof value === "string" && value.length > 0)
    )
  );

type ThicknessAnalysisResult = {
  dir: string;
  stub: string;
  runId: number;
};

type BottleHeavyFields = Pick<ThicknessBottle, "mean_thickness_profile" | "analysis_payload">;
type BottleMeanProfileFields = Pick<ThicknessBottle, "mean_thickness_profile">;

export default function ThicknessPage() {
  const { status: nasStatus, mountPoint, error: nasError, config, setConfig, triggerMount } = useNas();

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<ThicknessBottle[]>([]);
  const [filtered, setFiltered] = useState<ThicknessBottle[]>([]);
  const [filters, setFilters] = useState<ThicknessFilters>(defaultThicknessFilters);
  const [draftFilters, setDraftFilters] = useState<ThicknessFilters>(defaultThicknessFilters);
  const [searchTerm, setSearchTerm] = useState("");
  const [sort, setSort] = useState<ThicknessSortState>({ field: "timestamp", direction: "desc" });
  const [page, setPage] = useState(0);
  const [galleryPage, setGalleryPage] = useState(0);
  const [galleryCacheBustKey, setGalleryCacheBustKey] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [detailBottle, setDetailBottle] = useState<ThicknessBottle | null>(null);
  const [analysisPhase, setAnalysisPhase] = useState<AnalysisPhase>("idle");
  const [analysisStatusMessage, setAnalysisStatusMessage] = useState<string | null>(null);
  const [analysisProgress, setAnalysisProgress] = useState<AnalysisProgressState | null>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [analysisWarning, setAnalysisWarning] = useState<string | null>(null);
  const [analysisDurationMs, setAnalysisDurationMs] = useState<number | null>(null);
  const [analysisResult, setAnalysisResult] = useState<ThicknessAnalysisResult | null>(null);
  const [analysisTriggered, setAnalysisTriggered] = useState(false);
  const [analysisPayload, setAnalysisPayload] = useState<Record<string, unknown> | null>(null);
  const [analysisBundleHydrating, setAnalysisBundleHydrating] = useState(false);
  const [analysisViewportClass, setAnalysisViewportClass] = useState(
    viewportClassFromWidth(typeof window === "undefined" ? 1400 : window.innerWidth)
  );
  const [analysisBottleIds, setAnalysisBottleIds] = useState<number[]>([]);
  const [analysisName, setAnalysisName] = useState("");
  const [zoomSrc, setZoomSrc] = useState<string | null>(null);
  const [zoomAlt, setZoomAlt] = useState<string>("");
  const [analysisSaveBusy, setAnalysisSaveBusy] = useState(false);
  const [analysisSaveError, setAnalysisSaveError] = useState<string | null>(null);
  const [analysisExportBusy, setAnalysisExportBusy] = useState(false);
  const [analysisExportError, setAnalysisExportError] = useState<string | null>(null);
  const [savedAnalyses, setSavedAnalyses] = useState<SavedThicknessMultipleAnalysis[]>([]);
  const [showAllowedThicknessAnalysisIds, setShowAllowedThicknessAnalysisIds] = useState<Set<number>>(
    new Set()
  );
  const [showSelectedAnalysisId, setShowSelectedAnalysisId] = useState<number | null>(null);
  const [showLoadedAnalysisId, setShowLoadedAnalysisId] = useState<number | null>(null);
  const [showAnalysisLoading, setShowAnalysisLoading] = useState(false);
  const [showAnalysisHydrating, setShowAnalysisHydrating] = useState(false);
  const [showAnalysisStatus, setShowAnalysisStatus] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<SavedThicknessMultipleAnalysis | null>(null);
  const analyzeRunRef = useRef(0);
  const analyzeStartRef = useRef<number | null>(null);
  const [tick, forceTick] = useState(0);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncRunning, setSyncRunning] = useState(false);
  const [syncLogs, setSyncLogs] = useState<LogEntry[]>([]);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [pendingRecalcPrompt, setPendingRecalcPrompt] = useState<PendingRecalcPrompt | null>(null);
  const [schemaRecalcChoices, setSchemaRecalcChoices] = useState<Record<number, RecalcChoice>>({});
  const [syncSelectedBottleOverrideIds, setSyncSelectedBottleOverrideIds] = useState<number[] | null>(
    null
  );
  const [syncDryRun, setSyncDryRun] = useState(false);
  const [syncRecalcExisting, setSyncRecalcExisting] = useState(false);
  const [syncRecalcSelectedOnly, setSyncRecalcSelectedOnly] = useState(false);
  const [syncRecalcSavedAnalyses, setSyncRecalcSavedAnalyses] = useState(false);
  const [syncRecalcSavedRequireSingles, setSyncRecalcSavedRequireSingles] = useState(true);
  const [pendingRecalcSyncStart, setPendingRecalcSyncStart] = useState(false);
  const lastDryRunDirRef = useRef<string | null>(null);
  const [syncPaused, setSyncPaused] = useState(false);
  const [syncStopRequested, setSyncStopRequested] = useState(false);
  const syncControlRef = useRef({ pause: false, stop: false });
  const [syncProgress, setSyncProgress] = useState<SyncProgressState>(buildInitialSyncProgress);
  const syncLogSuppressedRef = useRef(false);

  const [nasCreds, setNasCreds] = useState(config);

  const analysisBusy = analysisPhase !== "idle";
  const analysisPreflighting = analysisPhase === "preflight";
  const analysisRunning = analysisPhase === "running";
  const showThicknessAnalyses = isShowMode
    ? savedAnalyses.filter((analysis) => showAllowedThicknessAnalysisIds.has(analysis.id))
    : savedAnalyses;
  const showAnalysisBusy = showAnalysisLoading || showAnalysisHydrating;
  const showHasLoadedAnalysis = !isShowMode || showLoadedAnalysisId != null;
  const schemaRecalcPrompt =
    pendingRecalcPrompt?.kind === "schema" ? pendingRecalcPrompt : null;
  const rawDataRecalcPrompt =
    pendingRecalcPrompt?.kind === "raw-data" ? pendingRecalcPrompt : null;
  const schemaPromptDetectedSchemas = schemaRecalcPrompt
    ? getDetectedSchemaLabels(schemaRecalcPrompt.bottleIds, schemaRecalcPrompt.schemaById)
    : [];
  const schemaPromptFailedIds = schemaRecalcPrompt?.failedBottleIds ?? [];
  const schemaPromptFailedSet = useMemo(
    () => new Set(schemaPromptFailedIds),
    [schemaPromptFailedIds]
  );
  const schemaPromptRemainingCount = schemaRecalcPrompt?.remainingBottleIds?.length ?? 0;
  const schemaPromptRecalcIds = schemaRecalcPrompt
    ? schemaRecalcPrompt.bottleIds.filter(
        (id) => (schemaRecalcChoices[id] ?? "recalc") === "recalc"
      )
    : [];
  const schemaPromptExcludeIds = schemaRecalcPrompt
    ? schemaRecalcPrompt.bottleIds.filter(
        (id) => (schemaRecalcChoices[id] ?? "recalc") === "exclude"
      )
    : [];
  const syncPresetIds = syncSelectedBottleOverrideIds ?? [];
  const [detailBundleHydratingIds, setDetailBundleHydratingIds] = useState<Set<number>>(new Set());
  const needsLogin = !isShowMode && nasStatus !== "mounted";

  useEffect(() => {
    const onResize = () => {
      setAnalysisViewportClass(viewportClassFromWidth(window.innerWidth));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (nasStatus === "mounted" || !mountPoint) {
      loadMetadata();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nasStatus, mountPoint]);

  const loadMetadata = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await fetchThicknessMetadata();
      setMetadata(data);
    } catch (err) {
      console.error(err);
      setLoadError(err instanceof Error ? err.message : "Failed to load thickness metadata");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const next = applyThicknessFilters(metadata, filters, searchTerm);
    const sorted = [...next].sort((a, b) => {
      const direction = sort.direction === "asc" ? 1 : -1;
      if (sort.field === "timestamp") {
        const ta = a.timestamp || "";
        const tb = b.timestamp || "";
        return (ta > tb ? 1 : -1) * direction;
      }
      if (sort.field === "trial_name") {
        return (a.trial_name > b.trial_name ? 1 : -1) * direction;
      }
      return (a.relative_bottle_id - b.relative_bottle_id) * direction;
    });
    setFiltered(sorted);
    setPage(0);
  }, [metadata, filters, sort, searchTerm]);

  const selectedBottles = useMemo(
    () => filtered.filter((row) => selectedIds.has(row.id)),
    [filtered, selectedIds]
  );
  const showTrialOptions = useMemo(
    () => collectTrialSelections(selectedBottles),
    [selectedBottles]
  );
  const showReadOnlyFilterLabels = useMemo(
    () => buildShowThicknessFilterLabels(selectedBottles),
    [selectedBottles]
  );
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(selectedBottles.length / 8));
    if (galleryPage >= totalPages) {
      setGalleryPage(totalPages - 1);
    }
  }, [selectedBottles, galleryPage]);

  useEffect(() => {
    if (!analysisRunning) return;
    const id = setInterval(() => {
      forceTick((n) => n + 1);
    }, 200);
    return () => clearInterval(id);
  }, [analysisRunning]);

  const runningSeconds = useMemo(() => {
    if (!analysisRunning || !analyzeStartRef.current) return null;
    return ((Date.now() - analyzeStartRef.current) / 1000).toFixed(1);
  }, [analysisRunning, tick]);

  const classifyLogLine = (line: string): LogTone => {
    const lower = line.toLowerCase();
    if (lower.includes("fail") || lower.includes("error")) return "error";
    if (
      lower.includes("missing") ||
      lower.includes("skipping") ||
      lower.includes("no data") ||
      lower.includes("no folders") ||
      lower.includes("stopping") ||
      lower.includes("stopped")
    ) {
      return "warn";
    }
    if (lower.includes("ok") || lower.includes("complete") || lower.includes("inserted")) {
      return "success";
    }
    return "info";
  };

  const appendSyncLog = (message: string, reset = false, tone?: LogTone, force = false) => {
    if (syncLogSuppressedRef.current && !force) return;
    const timestamp = formatCurrentDateTime();
    const formatted = String(message ?? "").replace(/\\n/g, "\n");
    const lines = formatted.split(/\n/);
    setSyncLogs((prev) => {
      const base = reset ? [] : prev;
      const next = lines.map((line) => ({
        message: `[${timestamp}] ${line}`,
        tone: tone || classifyLogLine(line)
      }));
      return [...base, ...next].slice(-500);
    });
  };

  useEffect(() => {
    if (!window.api?.onThicknessProcessProgress) return;
    return window.api.onThicknessProcessProgress((_event, data) => {
      if (syncLogSuppressedRef.current) return;
      const rawMessage = typeof data?.message === "string" ? data.message.trim() : "";
      if (!rawMessage) return;
      const trialName =
        typeof data?.trialDir === "string"
          ? data.trialDir.replace(/\\/g, "/").split("/").filter(Boolean).pop()
          : undefined;
      const bottleId = typeof data?.bottleId === "number" ? data.bottleId : undefined;
      const context =
        bottleId !== undefined
          ? trialName
            ? `Bottle ${bottleId} (${trialName})`
            : `Bottle ${bottleId}`
          : trialName || "Bottle worker";
      const level = data?.level;
      const tone: LogTone =
        level === "success" || level === "warn" || level === "error" || level === "info"
          ? level
          : "info";
      const timestamp = formatCurrentDateTime();
      setSyncLogs((prev) =>
        [
          ...prev,
          {
            message: `[${timestamp}] ${context}: ${rawMessage}`,
            tone
          }
        ].slice(-500)
      );
    });
  }, []);

  const clampPercent = (value: number) => Math.max(0, Math.min(100, value));

  const waitForSyncReady = async () => {
    if (syncControlRef.current.stop) return false;
    while (syncControlRef.current.pause) {
      if (syncControlRef.current.stop) return false;
      await new Promise((resolve) => setTimeout(resolve, SYNC_PAUSE_POLL_MS));
    }
    return !syncControlRef.current.stop;
  };

  const trialOptions = useMemo(() => {
    const knownTypes = ["Glaschemie", "Brennstoff", "Energieverhältnis", "Formgebung"];
    const base: Record<string, string[]> = {
      Glaschemie: [],
      Brennstoff: [],
      Energieverhältnis: [],
      Formgebung: []
    };
    const map = new Map<string, Set<string>>();
    metadata.forEach((row) => {
      (row.active_trials || []).forEach((trial) => {
        if (!knownTypes.includes(trial.trial_type)) return;
        const set = map.get(trial.trial_type) || new Set<string>();
        set.add(trial.descriptive_name);
        map.set(trial.trial_type, set);
      });
    });
    map.forEach((value, key) => {
      base[key] = Array.from(value.values()).sort();
    });
    return base;
  }, [metadata]);

  const refreshThicknessAnalyses = async () => {
    setAnalysisSaveError(null);
    try {
      clearCache();
      const analyses = await loadThicknessMultipleAnalyses();
      setSavedAnalyses(analyses);
    } catch (err) {
      setAnalysisSaveError(
        err instanceof Error ? err.message : "Failed to load saved thickness analyses"
      );
    }
  };

  useEffect(() => {
    if (isShowMode) return;
    void refreshThicknessAnalyses();
  }, [isShowMode]);

  const loadShowThicknessAnalyses = async () => {
    setShowAnalysisLoading(true);
    setShowAnalysisStatus(null);
    try {
      const configResp = await fetch(SHOW_FILTER_SELECTION_PATH, {
        cache: "no-store"
      });
      if (!configResp.ok) {
        throw new Error(
          `Show preset config not found (${SHOW_FILTER_SELECTION_PATH}). HTTP ${configResp.status}`
        );
      }

      const config = (await configResp.json()) as ShowFilterSelection;
      const allowedIds = new Set(
        Array.isArray(config.thickness_analysis_ids)
          ? config.thickness_analysis_ids
              .map((value) => Number(value))
              .filter((value) => Number.isFinite(value) && value > 0)
          : []
      );
      if (!allowedIds.size) {
        throw new Error(
          "Show preset config has no thickness_analysis_ids. Publish profile must define at least one preset."
        );
      }

      clearCache();
      const analyses = await loadThicknessMultipleAnalyses();
      const filteredAnalyses = analyses.filter((analysis) => allowedIds.has(analysis.id));
      if (!filteredAnalyses.length) {
        throw new Error(
          "No matching thickness presets found in database for thickness_analysis_ids from show config."
        );
      }

      setShowAllowedThicknessAnalysisIds(allowedIds);
      setSavedAnalyses(filteredAnalyses);
      setShowSelectedAnalysisId((prev) => {
        if (prev != null && filteredAnalyses.some((analysis) => analysis.id === prev)) return prev;
        return filteredAnalyses[0]?.id ?? null;
      });
      setShowLoadedAnalysisId((prev) => {
        if (prev != null && filteredAnalyses.some((analysis) => analysis.id === prev)) return prev;
        return null;
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load show thickness presets.";
      setShowAnalysisStatus(message);
      setShowAllowedThicknessAnalysisIds(new Set());
      setSavedAnalyses([]);
      setShowSelectedAnalysisId(null);
      setShowLoadedAnalysisId(null);
    } finally {
      setShowAnalysisLoading(false);
    }
  };

  useEffect(() => {
    if (!isShowMode) {
      setShowAllowedThicknessAnalysisIds(new Set());
      setShowSelectedAnalysisId(null);
      setShowLoadedAnalysisId(null);
      setShowAnalysisStatus(null);
      setShowAnalysisLoading(false);
      setShowAnalysisHydrating(false);
      return;
    }
    void loadShowThicknessAnalyses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShowMode]);

  const bottleNeedsHeavyFields = (bottle: ThicknessBottle | null | undefined) =>
    !!bottle && (bottle.mean_thickness_profile == null || bottle.analysis_payload == null);

  const hydrateBottleHeavyFields = async (ids: number[]): Promise<Map<number, BottleHeavyFields>> => {
    const uniqueIds = Array.from(
      new Set(ids.map((id) => Number(id)).filter((id) => Number.isFinite(id)))
    );
    const heavyById = new Map<number, BottleHeavyFields>();
    if (!uniqueIds.length) return heavyById;

    const metadataById = new Map<number, ThicknessBottle>(metadata.map((row) => [row.id, row]));
    const missing = uniqueIds.filter((id) => {
      const row = metadataById.get(id);
      if (!row) return true;
      if (!bottleNeedsHeavyFields(row)) {
        heavyById.set(id, {
          mean_thickness_profile: row.mean_thickness_profile ?? null,
          analysis_payload: row.analysis_payload ?? null
        });
        return false;
      }
      return true;
    });

    if (!missing.length) return heavyById;

    try {
      const fetched = await fetchThicknessBottleHeavy(missing, { includeAnalysisPayload: true });
      const fetchedById = new Map<number, BottleHeavyFields>(
        fetched.map((row) => [
          row.id,
          {
            mean_thickness_profile: row.mean_thickness_profile ?? null,
            analysis_payload: row.analysis_payload ?? null
          }
        ])
      );
      if (fetchedById.size) {
        setMetadata((prev) =>
          prev.map((row) => {
            const heavy = fetchedById.get(row.id);
            return heavy ? { ...row, ...heavy } : row;
          })
        );
        setDetailBottle((prev) => {
          if (!prev) return prev;
          const heavy = fetchedById.get(prev.id);
          return heavy ? { ...prev, ...heavy } : prev;
        });
      }
      fetchedById.forEach((value, key) => heavyById.set(key, value));
    } catch (err) {
      console.warn("Bottle heavy hydration failed", err);
    }

    return heavyById;
  };

  const hydrateBottleMeanProfiles = async (
    ids: number[]
  ): Promise<Map<number, BottleMeanProfileFields>> => {
    const uniqueIds = Array.from(
      new Set(ids.map((id) => Number(id)).filter((id) => Number.isFinite(id)))
    );
    const meanById = new Map<number, BottleMeanProfileFields>();
    if (!uniqueIds.length) return meanById;

    const metadataById = new Map<number, ThicknessBottle>(metadata.map((row) => [row.id, row]));
    const missing = uniqueIds.filter((id) => {
      const row = metadataById.get(id);
      if (!row) return true;
      if (row.mean_thickness_profile != null) {
        meanById.set(id, {
          mean_thickness_profile: row.mean_thickness_profile
        });
        return false;
      }
      return true;
    });

    if (!missing.length) return meanById;

    try {
      const fetched = await fetchThicknessBottleHeavy(missing, { includeAnalysisPayload: false });
      const fetchedById = new Map<number, BottleMeanProfileFields>(
        fetched.map((row) => [
          row.id,
          {
            mean_thickness_profile: row.mean_thickness_profile ?? null
          }
        ])
      );
      if (fetchedById.size) {
        setMetadata((prev) =>
          prev.map((row) => {
            const mean = fetchedById.get(row.id);
            return mean ? { ...row, ...mean } : row;
          })
        );
        setDetailBottle((prev) => {
          if (!prev) return prev;
          const mean = fetchedById.get(prev.id);
          return mean ? { ...prev, ...mean } : prev;
        });
      }
      fetchedById.forEach((value, key) => meanById.set(key, value));
    } catch (err) {
      console.warn("Bottle mean-profile hydration failed", err);
    }

    return meanById;
  };

  const hydrateSavedAnalysisHeavy = async (
    analysisId: number,
    fallbackAnalysis?: SavedThicknessMultipleAnalysis
  ): Promise<SavedThicknessMultipleAnalysis | null> => {
    const existing = savedAnalyses.find((row) => row.id === analysisId) || fallbackAnalysis || null;
    if (
      existing &&
      existing.mean_thickness_profile != null &&
      existing.analysis_payload != null
    ) {
      return existing;
    }

    try {
      const heavy = await fetchThicknessMultipleAnalysisHeavy(analysisId);
      if (!heavy) return existing;
      const normalized = {
        mean_thickness_profile: heavy.mean_thickness_profile ?? null,
        analysis_payload: heavy.analysis_payload ?? null
      };
      const merged = existing ? { ...existing, ...normalized } : null;
      setSavedAnalyses((prev) =>
        prev.map((row) => (row.id === analysisId ? { ...row, ...normalized } : row))
      );
      return merged;
    } catch (err) {
      console.warn("Saved analysis heavy hydration failed", err);
      return existing;
    }
  };

  const analysisPaths = useMemo(() => buildAnalysisPaths(analysisResult), [analysisResult]);
  const analysisContract = useMemo<ThicknessAnalysisContract | null>(
    () => getAnalysisContractForViewport(analysisPayload, analysisViewportClass),
    [analysisPayload, analysisViewportClass]
  );
  const analysisHasPlotBundle = useMemo(
    () => hasPlotBundle(analysisContract),
    [analysisContract]
  );
  const analysisMatchesSelection =
    analysisBottleIds.length > 0 &&
    analysisBottleIds.length === selectedIds.size &&
    analysisBottleIds.every((id) => selectedIds.has(id));
  const canSaveAnalysis =
    !!analysisResult &&
    !!analysisPayload &&
    analysisMatchesSelection &&
    analysisName.trim().length > 0 &&
    !analysisSaveBusy &&
    !!mountPoint &&
    !analysisResult.dir.startsWith(mountPoint) &&
    !!window.api?.movePath;

  useEffect(() => {
    if (!analysisTriggered || !selectedBottles.length) return;
    void hydrateBottleMeanProfiles(selectedBottles.map((row) => row.id));
  }, [analysisTriggered, selectedBottles]);

  const handleSelectToggle = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setGalleryPage(0);
  };

  const handleSelectAll = (ids: number[]) => {
    setSelectedIds(new Set(ids));
    setGalleryPage(0);
  };

  const handleApplyFilters = () => {
    setFilters(draftFilters);
  };

  const handleClearFilters = () => {
    setDraftFilters(defaultThicknessFilters);
    setFilters(defaultThicknessFilters);
    setSearchTerm("");
  };

  const handleRestoreAnalysis = (analysis: SavedThicknessMultipleAnalysis) => {
    const restoredIds = analysis.bottle_id_array
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));
    if (!restoredIds.length) {
      setAnalysisSaveError("Saved analysis has no bottle IDs to load.");
      return;
    }
    const existingPayload =
      analysis.analysis_payload && typeof analysis.analysis_payload === "object"
        ? (analysis.analysis_payload as Record<string, unknown>)
        : null;
    const runId = analyzeRunRef.current + 1;
    analyzeRunRef.current = runId;
    analyzeStartRef.current = null;

    setAnalysisSaveError(null);
    setAnalysisExportError(null);
    setDraftFilters(defaultThicknessFilters);
    setFilters(defaultThicknessFilters);
    setSearchTerm("");
    setPage(0);
    setSelectedIds(new Set(restoredIds));
    setGalleryPage(0);
    setAnalysisTriggered(true);
    setAnalysisError(null);
    setAnalysisWarning(null);
    setAnalysisDurationMs(null);
    setAnalysisPhase("idle");
    setAnalysisStatusMessage(null);
    setAnalysisProgress(null);
    setPendingRecalcPrompt(null);
    setSchemaRecalcChoices({});
    setAnalysisPayload(existingPayload);
    setAnalysisBundleHydrating(!existingPayload);
    setAnalysisBottleIds(restoredIds);

    if (mountPoint) {
      const outputDir = joinNasPath(
        mountPoint,
        NAS_THICKNESS_MULTIPLE_DIR,
        buildMultipleAnalysisFolderName(analysis.id, analysis.name)
      );
      setAnalysisResult({ dir: outputDir, stub: THICKNESS_ANALYSIS_STUB, runId });
    } else {
      setAnalysisResult(null);
      if (!existingPayload) {
        setAnalysisSaveError("Mount the NAS to load saved analysis output files.");
      }
    }

    if (!existingPayload) {
      void hydrateSavedAnalysisHeavy(analysis.id, analysis).then((hydrated) => {
        if (analyzeRunRef.current !== runId) return;
        if (hydrated?.analysis_payload && typeof hydrated.analysis_payload === "object") {
          setAnalysisPayload(hydrated.analysis_payload as Record<string, unknown>);
          setAnalysisSaveError(null);
        }
        if (!mountPoint && !hydrated?.analysis_payload) {
          setAnalysisSaveError("Mount the NAS to load saved analysis output files.");
        }
        setAnalysisBundleHydrating(false);
      });
    }
  };

  const handleLoadShowAnalysis = async (analysisId: number | null) => {
    setShowAnalysisStatus(null);
    setAnalysisSaveError(null);
    setAnalysisError(null);
    if (analysisId == null) {
      handleReset();
      setShowLoadedAnalysisId(null);
      return;
    }

    const analysis = showThicknessAnalyses.find((entry) => entry.id === analysisId);
    if (!analysis) {
      setShowAnalysisStatus("Selected preset is not available in this published set.");
      return;
    }

    const restoredIds = analysis.bottle_id_array
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));
    const metadataById = new Map(metadata.map((row) => [row.id, row]));
    const rows = restoredIds.map((id) => metadataById.get(id)).filter(Boolean) as ThicknessBottle[];
    if (rows.length !== restoredIds.length) {
      setShowAnalysisStatus("The selected preset is missing bottle metadata in this published set.");
      return;
    }

    setShowAnalysisHydrating(true);
    try {
      const hydrated = await hydrateSavedAnalysisHeavy(analysis.id, analysis);
      const completeAnalysis = hydrated || analysis;
      if (!completeAnalysis.analysis_payload || typeof completeAnalysis.analysis_payload !== "object") {
        throw new Error("The selected preset has no stored plot bundle in this published set.");
      }
      const readonlyFilters = buildShowThicknessFilters(rows);
      handleRestoreAnalysis(completeAnalysis);
      setDraftFilters(readonlyFilters);
      setFilters(readonlyFilters);
      setShowLoadedAnalysisId(completeAnalysis.id);
    } catch (err) {
      setShowAnalysisStatus(err instanceof Error ? err.message : "Failed to load selected preset.");
    } finally {
      setShowAnalysisHydrating(false);
    }
  };

  const handleViewDetail = (bottle: ThicknessBottle) => {
    setDetailBottle(bottle);
    if (!bottleNeedsHeavyFields(bottle)) return;
    setDetailBundleHydratingIds((prev) => new Set(prev).add(bottle.id));
    void hydrateBottleHeavyFields([bottle.id]).finally(() => {
      setDetailBundleHydratingIds((prev) => {
        const next = new Set(prev);
        next.delete(bottle.id);
        return next;
      });
    });
  };

  const handleDeleteAnalysis = async (id: number) => {
    setAnalysisSaveBusy(true);
    setAnalysisSaveError(null);
    try {
      await deleteThicknessMultipleAnalysis(id);
      await refreshThicknessAnalyses();
    } catch (err) {
      setAnalysisSaveError(err instanceof Error ? err.message : "Failed to delete analysis.");
    } finally {
      setAnalysisSaveBusy(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    await handleDeleteAnalysis(pendingDelete.id);
    setPendingDelete(null);
  };

  const handleSaveAnalysis = async () => {
    if (analysisSaveBusy) return;
    if (!analysisPayload || !analysisResult) {
      setAnalysisSaveError("Run Analyze before saving a multiple analysis.");
      return;
    }
    if (!analysisMatchesSelection) {
      setAnalysisSaveError("Selection changed since the last analysis. Run Analyze again before saving.");
      return;
    }
    if (!window.api?.movePath) {
      setAnalysisSaveError("Saving analyses requires the desktop runtime.");
      return;
    }
    if (!mountPoint) {
      setAnalysisSaveError("Mount the NAS before saving this analysis.");
      return;
    }
    if (analysisResult.dir.startsWith(mountPoint)) {
      setAnalysisSaveError("Analysis outputs are already stored on the NAS. Run Analyze again to save a new copy.");
      return;
    }

    setAnalysisSaveBusy(true);
    setAnalysisSaveError(null);
    const bottleIds = analysisBottleIds;
    try {
      const payload = buildMultipleAnalysisPayload(analysisPayload, analysisName, bottleIds);
      const saved = await saveThicknessMultipleAnalysis(payload);
      const outputDir = joinNasPath(
        mountPoint,
        NAS_THICKNESS_MULTIPLE_DIR,
        buildMultipleAnalysisFolderName(saved.id, saved.name)
      );
      const moveResult = await window.api.movePath(analysisResult.dir, outputDir);
      if (!moveResult.ok) {
        await deleteThicknessMultipleAnalysis(saved.id).catch(() => {});
        throw new Error(moveResult.message || "Failed to move analysis outputs to NAS.");
      }

      const tmpRoot = deriveTmpRoot(analysisResult.dir);
      const analysisJson = `${tmpRoot}/${THICKNESS_ANALYSIS_STUB}.json`;
      const payloadJson = `${tmpRoot}/${THICKNESS_ANALYSIS_STUB}_payload.json`;
      const movedAnalysisJson = await window.api.movePath(
        analysisJson,
        joinNasPath(outputDir, `${THICKNESS_ANALYSIS_STUB}.json`)
      );
      const movedPayloadJson = await window.api.movePath(
        payloadJson,
        joinNasPath(outputDir, `${THICKNESS_ANALYSIS_STUB}_payload.json`)
      );
      if (!movedAnalysisJson.ok || !movedPayloadJson.ok) {
        console.warn("Saved analysis outputs moved, but metadata files were not relocated.");
      }

      setAnalysisResult({
        dir: outputDir,
        stub: THICKNESS_ANALYSIS_STUB,
        runId: analysisResult.runId
      });
      setAnalysisName("");
      await refreshThicknessAnalyses();
    } catch (err) {
      setAnalysisSaveError(err instanceof Error ? err.message : "Failed to save analysis.");
    } finally {
      setAnalysisSaveBusy(false);
    }
  };

  const handleExportAnalysisBundle = async () => {
    if (analysisExportBusy) return;
    if (!analysisResult && !analysisPayload && !analysisContract) {
      setAnalysisExportError("Run or load a combined thickness analysis before exporting.");
      return;
    }

    setAnalysisExportBusy(true);
    setAnalysisExportError(null);

    try {
      const warnings: string[] = [];
      const entries: ZipEntryInput[] = [];
      const addEntry = buildZipEntryAdder(entries, warnings);
      const bottleIds = (analysisBottleIds.length ? analysisBottleIds : Array.from(selectedIds))
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id));
      const outputStub = analysisResult?.stub || THICKNESS_ANALYSIS_STUB;

      if (analysisResult) {
        await addThicknessOutputFiles(addEntry, warnings, analysisResult);
      }

      if (analysisPayload) {
        addEntry("metadata/analysis_payload_from_database.json", formatJson(analysisPayload));
      }
      if (analysisContract) {
        addEntry("metadata/analysis_contract.json", formatJson(analysisContract));
        if (Array.isArray(analysisContract.mean_thickness_profile)) {
          addEntry(
            "data/mean_thickness_profile.csv",
            recordsToCsv(analysisContract.mean_thickness_profile, ["height", "thickness"])
          );
        }
        if (Array.isArray(analysisContract.measurement_ci_profile)) {
          addEntry(
            "data/measurement_ci_profile.csv",
            recordsToCsv(analysisContract.measurement_ci_profile, ["height", "ci_low", "ci_high"])
          );
        }
        if (analysisContract.metrics && typeof analysisContract.metrics === "object") {
          addEntry("metadata/metrics.json", formatJson(analysisContract.metrics));
        }
      }

      if (bottleIds.length) {
        try {
          const dataMap = await fetchThicknessData(bottleIds);
          const averagedRows = averageThicknessData(dataMap);
          if (averagedRows.length) {
            addEntry("data/averaged_thickness_data.csv", recordsToCsv(averagedRows));
          }
          Object.entries(dataMap)
            .sort(([a], [b]) => Number(a) - Number(b))
            .forEach(([id, rows]) => {
              if (!Array.isArray(rows) || rows.length === 0) return;
              addEntry(`data/source_bottle_${id}_thickness_data.csv`, recordsToCsv(rows));
            });
        } catch (err) {
          warnings.push(
            `Thickness data CSV export failed: ${
              err instanceof Error ? err.message : "unknown error"
            }`
          );
        }
      } else {
        warnings.push("No bottle IDs were available for raw thickness CSV export.");
      }

      const hasCsv = entries.some((entry) => entry.path.toLowerCase().endsWith(".csv"));
      if (!hasCsv) {
        warnings.push("No CSV data could be generated for this analysis.");
      }

      addEntry(
        "manifest.json",
        formatJson({
          exported_at: new Date().toISOString(),
          output_dir: analysisResult?.dir ?? null,
          output_stub: outputStub,
          bottle_ids: bottleIds,
          files: entries.map((entry) => entry.path),
          warnings
        })
      );

      if (entries.length <= 1) {
        throw new Error("No analysis files or data were available to export.");
      }

      const zip = createZipBlob(entries);
      downloadBlob(zip, `${buildThicknessBundleFileName(bottleIds)}.zip`);
    } catch (err) {
      setAnalysisExportError(
        err instanceof Error ? err.message : "Failed to export combined thickness bundle."
      );
    } finally {
      setAnalysisExportBusy(false);
    }
  };

  const handleReset = () => {
    handleClearFilters();
    setSort({ field: "timestamp", direction: "desc" });
    setPage(0);
    setSelectedIds(new Set());
    setGalleryPage(0);
    setAnalysisResult(null);
    setAnalysisError(null);
    setAnalysisWarning(null);
    setAnalysisDurationMs(null);
    setAnalysisPhase("idle");
    setAnalysisStatusMessage(null);
    setAnalysisProgress(null);
    setAnalysisTriggered(false);
    setAnalysisPayload(null);
    setAnalysisBundleHydrating(false);
    setAnalysisBottleIds([]);
    setAnalysisName("");
    setAnalysisSaveError(null);
    setAnalysisExportError(null);
    setPendingRecalcPrompt(null);
    setSchemaRecalcChoices({});
    setSyncSelectedBottleOverrideIds(null);
    setShowSelectedAnalysisId(isShowMode ? showThicknessAnalyses[0]?.id ?? null : null);
    setShowLoadedAnalysisId(null);
    analyzeStartRef.current = null;
    analyzeRunRef.current += 1;
  };

  const handleOpenSync = () => {
    if (isShowMode) return;
    setSyncOpen(true);
    setSyncError(null);
    setSyncSelectedBottleOverrideIds(null);
  };

  const handleCloseSync = () => {
    if (syncRunning) return;
    setPendingRecalcSyncStart(false);
    setSyncOpen(false);
    setSyncSelectedBottleOverrideIds(null);
  };

  const openSyncWithSelectedRecalcPreset = (bottleIds?: number[]) => {
    const normalizedIds = Array.from(
      new Set((bottleIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id)))
    ).sort((a, b) => a - b);
    setSyncDryRun(false);
    setSyncRecalcExisting(true);
    setSyncRecalcSelectedOnly(true);
    setSyncRecalcSavedAnalyses(false);
    setSyncSelectedBottleOverrideIds(normalizedIds.length ? normalizedIds : null);
    setSyncOpen(true);
    setSyncError(null);
  };

  useEscapeStack(syncOpen && !syncRunning, handleCloseSync);
  useEscapeStack(!!schemaRecalcPrompt && !syncRunning, () => setPendingRecalcPrompt(null));
  useEscapeStack(!!zoomSrc, () => setZoomSrc(null));

  const handleToggleSyncPause = () => {
    if (!syncRunning) return;
    const next = !syncControlRef.current.pause;
    syncControlRef.current.pause = next;
    setSyncPaused(next);
    appendSyncLog(next ? "Sync paused." : "Sync resumed.");
  };

  const handleStopSync = () => {
    if (!syncRunning || syncStopRequested) return;
    syncControlRef.current.stop = true;
    syncControlRef.current.pause = false;
    setSyncStopRequested(true);
    setSyncPaused(false);
    setSyncError(null);
    syncLogSuppressedRef.current = true;
    setSyncLogs([]);
    setSyncProgress(buildInitialSyncProgress());
  };

  const deleteDryRunDir = async (dirPath: string, reason: string) => {
    if (!window.api?.deletePath) {
      appendSyncLog("Dry run cleanup requires the desktop runtime.");
      return;
    }
    const resp = await window.api.deletePath(dirPath);
    if (!resp.ok) {
      appendSyncLog(
        `Dry run cleanup failed for ${dirPath}: ${resp.message || "unknown error"}`,
        false,
        "error"
      );
      return;
    }
    appendSyncLog(`Dry run cleanup: removed ${dirPath} (${reason}).`);
  };

  const handleClearDryRunOutputs = async () => {
    if (!mountPoint) {
      appendSyncLog("Dry run cleanup skipped: NAS is not mounted.");
      return;
    }
    if (!window.api?.deletePath) {
      appendSyncLog("Dry run cleanup requires the desktop runtime.");
      return;
    }
    const dryRunRoot = joinNasPath(mountPoint, NAS_THICKNESS_DRY_RUN_DIR);
    const resp = await window.api.deletePath(dryRunRoot);
    if (!resp.ok) {
      appendSyncLog(`Dry run cleanup failed: ${resp.message || "unknown error"}`, false, "error");
      return;
    }
    appendSyncLog(`Dry run cleanup: removed ${dryRunRoot}.`, false, "success");
    lastDryRunDirRef.current = null;
  };

  const normalizeBottlePayload = (
    raw: Record<string, unknown>,
    fallbackTrial: string,
    fallbackId: number
  ): ThicknessBottlePayload => {
    const toNumber = (value: unknown) => {
      const num = Number(value);
      return Number.isFinite(num) ? num : undefined;
    };

    const trialName = typeof raw.trial_name === "string" ? raw.trial_name : fallbackTrial;
    const bottleId = Number(raw.relative_bottle_id ?? fallbackId);
    const bottleProfile =
      typeof raw.bottle_profile === "string" ? raw.bottle_profile : "";
    const numberOfSlices = Number(raw.number_of_slices ?? 0);

    if (!trialName || !Number.isFinite(bottleId)) {
      throw new Error("Invalid thickness payload: missing trial name or bottle id.");
    }
    if (!bottleProfile || !Number.isFinite(numberOfSlices) || numberOfSlices <= 0) {
      throw new Error("Invalid thickness payload: missing bottle profile or slice count.");
    }

    const payload: ThicknessBottlePayload = {
      trial_name: trialName,
      relative_bottle_id: bottleId,
      timestamp: typeof raw.timestamp === "string" ? raw.timestamp : null,
      bottle_profile: bottleProfile,
      number_of_slices: numberOfSlices,
      inner_volume: toNumber(raw.inner_volume),
      outer_volume: toNumber(raw.outer_volume),
      glass_volume: toNumber(raw.glass_volume),
      glass_mass: toNumber(raw.glass_mass),
      global_min_x: toNumber(raw.global_min_x),
      global_min_y: toNumber(raw.global_min_y),
      global_min_z: toNumber(raw.global_min_z),
      global_max_x: toNumber(raw.global_max_x),
      global_max_y: toNumber(raw.global_max_y),
      global_max_z: toNumber(raw.global_max_z),
      slice_min: Array.isArray(raw.slice_min) ? (raw.slice_min as number[][]) : undefined,
      slice_max: Array.isArray(raw.slice_max) ? (raw.slice_max as number[][]) : undefined,
      feeder_wave: Array.isArray(raw.feeder_wave) ? (raw.feeder_wave as number[][]) : undefined,
      data: Array.isArray(raw.data) ? (raw.data as ThicknessDataPoint[]) : undefined,
      mean_thickness_profile: Array.isArray(raw.mean_thickness_profile)
        ? (raw.mean_thickness_profile as { height: number; thickness: number }[])
        : undefined,
      analysis_payload:
        raw.analysis_payload && typeof raw.analysis_payload === "object"
          ? (raw.analysis_payload as ThicknessAnalysisContract)
          : undefined
    };

    return payload;
  };

  const buildMultipleAnalysisPayload = (
    raw: Record<string, unknown>,
    name: string,
    bottleIds: number[]
  ): ThicknessMultipleAnalysisPayload => {
    const requireNumber = (value: unknown, label: string) => {
      const parsed = Number(value);
      if (!Number.isFinite(parsed)) {
        throw new Error(`Missing ${label} in analysis payload.`);
      }
      return parsed;
    };

    const trimmedName = name.trim();
    if (!trimmedName) {
      throw new Error("Provide a name for this analysis.");
    }
    if (!bottleIds.length) {
      throw new Error("Select at least one bottle before saving.");
    }

    const bottleProfile = typeof raw.bottle_profile === "string" ? raw.bottle_profile : "";
    if (!bottleProfile) {
      throw new Error("Missing bottle_profile in analysis payload.");
    }

    const analysisContractRaw = (() => {
      if (typeof raw.schema_version === "string") return raw;
      if (
        typeof raw.analysis_payload === "object" &&
        raw.analysis_payload !== null &&
        !Array.isArray(raw.analysis_payload)
      ) {
        return raw.analysis_payload as Record<string, unknown>;
      }
      return null;
    })();

    const meanProfileFromRaw = Array.isArray(raw.mean_thickness_profile)
      ? raw.mean_thickness_profile
      : null;
    const meanProfileFromContract =
      analysisContractRaw && Array.isArray(analysisContractRaw.mean_thickness_profile)
        ? analysisContractRaw.mean_thickness_profile
        : null;
    const meanThicknessProfile = (
      (meanProfileFromRaw || meanProfileFromContract || []) as unknown[]
    )
      .map((entry) => {
        if (!entry || typeof entry !== "object" || Array.isArray(entry)) return null;
        const rec = entry as Record<string, unknown>;
        const height = Number(rec.height);
        const thickness = Number(rec.thickness);
        if (!Number.isFinite(height) || !Number.isFinite(thickness)) return null;
        return { height, thickness };
      })
      .filter((entry): entry is { height: number; thickness: number } => !!entry);

    return {
      name: trimmedName,
      bottle_id_array: bottleIds,
      bottle_profile: bottleProfile,
      number_of_slices: requireNumber(raw.number_of_slices, "number_of_slices"),
      inner_volume: requireNumber(raw.inner_volume, "inner_volume"),
      outer_volume: requireNumber(raw.outer_volume, "outer_volume"),
      glass_volume: requireNumber(raw.glass_volume, "glass_volume"),
      glass_mass: requireNumber(raw.glass_mass, "glass_mass"),
      global_min_x: requireNumber(raw.global_min_x, "global_min_x"),
      global_min_y: requireNumber(raw.global_min_y, "global_min_y"),
      global_min_z: requireNumber(raw.global_min_z, "global_min_z"),
      global_max_x: requireNumber(raw.global_max_x, "global_max_x"),
      global_max_y: requireNumber(raw.global_max_y, "global_max_y"),
      global_max_z: requireNumber(raw.global_max_z, "global_max_z"),
      slice_min: Array.isArray(raw.slice_min) ? (raw.slice_min as number[][]) : undefined,
      slice_max: Array.isArray(raw.slice_max) ? (raw.slice_max as number[][]) : undefined,
      feeder_wave: Array.isArray(raw.feeder_wave) ? (raw.feeder_wave as number[][]) : undefined,
      data: Array.isArray(raw.data) ? (raw.data as ThicknessDataPoint[]) : undefined,
      mean_thickness_profile: meanThicknessProfile,
      analysis_payload: analysisContractRaw as ThicknessAnalysisContract | null
    };
  };

  const validateOutputFiles = async (outputDir: string, stub: string) => {
    if (!window.api?.listNasDir) return;
    const listing = await window.api.listNasDir(outputDir);
    if (!listing.ok || !listing.entries) {
      appendSyncLog(
        `Output check: unable to list ${outputDir} (${listing.message || "unknown error"})`,
        false,
        "error"
      );
      return;
    }
    const found = new Set(listing.entries.filter((entry) => entry.isFile).map((entry) => entry.name));
    const missing = THICKNESS_OUTPUT_SUFFIXES.filter((suffix) => !found.has(`${stub}${suffix}`));
    if (missing.length) {
      appendSyncLog(`Output check: missing ${missing.join(", ")}`, false, "error");
    } else {
      appendSyncLog("Output check: all expected files present.", false, "success");
    }
  };

  const probeThicknessRuntimeSchema = async (): Promise<{
    ok: boolean;
    message?: string;
    singleSchemaVersion?: string | null;
    multipleSchemaVersion?: string | null;
  }> => {
    if (!window.api?.getThicknessRuntimeSchemaVersion) {
      return { ok: true };
    }
    const result = await window.api.getThicknessRuntimeSchemaVersion();
    if (!result?.ok) {
      return {
        ok: false,
        message:
          result?.message ||
          "Failed to verify local thickness runtime schema version. Restart the app and retry."
      };
    }
    const singleSchemaVersion =
      typeof result.singleSchemaVersion === "string" ? result.singleSchemaVersion : null;
    const multipleSchemaVersion =
      typeof result.multipleSchemaVersion === "string" ? result.multipleSchemaVersion : null;
    if (
      singleSchemaVersion !== THICKNESS_ANALYSIS_SCHEMA_VERSION ||
      multipleSchemaVersion !== THICKNESS_ANALYSIS_SCHEMA_VERSION
    ) {
      return {
        ok: false,
        message:
          `Local thickness runtime is outdated (single=${singleSchemaVersion || "missing"}, multiple=${multipleSchemaVersion || "missing"}, ` +
          `expected=${THICKNESS_ANALYSIS_SCHEMA_VERSION}). Rebuild wheelhouse and restart the app.`,
        singleSchemaVersion,
        multipleSchemaVersion
      };
    }
    return { ok: true, singleSchemaVersion, multipleSchemaVersion };
  };

  const ensureThicknessRuntimeSchemaCurrent = async (
    options?: { onLog?: (message: string, tone?: LogTone) => void }
  ): Promise<{
    ok: boolean;
    message?: string;
    singleSchemaVersion?: string | null;
    multipleSchemaVersion?: string | null;
    refreshed?: boolean;
  }> => {
    const firstProbe = await probeThicknessRuntimeSchema();
    if (firstProbe.ok) {
      return { ...firstProbe, refreshed: false };
    }
    if (!window.api?.retryStartup) {
      return firstProbe;
    }

    options?.onLog?.(
      "Local thickness runtime schema is outdated. Attempting automatic runtime refresh...",
      "warn"
    );
    try {
      await window.api.retryStartup();
    } catch (err) {
      const message = err instanceof Error ? err.message : "unknown startup retry error";
      options?.onLog?.(`Automatic runtime refresh failed: ${message}`, "error");
      return firstProbe;
    }

    const secondProbe = await probeThicknessRuntimeSchema();
    if (secondProbe.ok) {
      options?.onLog?.("Automatic runtime refresh succeeded.", "success");
      return { ...secondProbe, refreshed: true };
    }

    return {
      ...secondProbe,
      message:
        secondProbe.message ||
        firstProbe.message ||
        "Local thickness runtime schema is outdated after automatic refresh."
    };
  };

  const handleRequestStartSync = () => {
    if (syncRunning) return;
    if (syncRecalcExisting || syncRecalcSavedAnalyses) {
      setPendingRecalcSyncStart(true);
      return;
    }
    void handleStartSync();
  };

  const handleStartSync = async () => {
    if (syncRunning) return;
    setSyncRunning(true);
    setSyncError(null);
    setSyncPaused(false);
    setSyncStopRequested(false);
    syncControlRef.current = { pause: false, stop: false };
    syncLogSuppressedRef.current = false;
    setSyncProgress(buildInitialSyncProgress());
    appendSyncLog("Sync started.", true);
    const log = (message: string, tone?: LogTone) => appendSyncLog(message, false, tone);
    const dryRun = syncDryRun;
    const recalcExisting = syncRecalcExisting;
    const recalcSelectedOnly = syncRecalcSelectedOnly;
    const recalcSavedAnalyses = syncRecalcSavedAnalyses;
    const requireSavedSinglesCurrent = syncRecalcSavedRequireSingles;
    const savedOnlyMode = recalcSavedAnalyses && !recalcExisting;
    const selectedOverrideIds =
      recalcExisting && recalcSelectedOnly && Array.isArray(syncSelectedBottleOverrideIds)
        ? syncSelectedBottleOverrideIds
        : null;
    setPendingRecalcSyncStart(false);

    log(dryRun ? "Mode: dry run (no DB writes)." : "Mode: live sync.");
    if (recalcExisting) {
      log(
        recalcSelectedOnly
          ? "Recalc mode: selected bottles only (existing rows will be recalculated, new rows inserted)."
          : "Recalc mode: all discovered bottles (existing rows will be recalculated, new rows inserted).",
        "warn"
      );
      if (recalcSelectedOnly) {
        log("Selected-only recalc forces recomputation for selected bottles (up-to-date guard disabled).");
        if (selectedOverrideIds?.length) {
          log(
            `Selected-only recalc override active: ${selectedOverrideIds.length} bottle(s) from analysis warning preset.`
          );
        }
      }
    }
    if (recalcSavedAnalyses) {
      log("Recalc mode: saved multiple analyses will be recalculated in-place.", "warn");
      if (savedOnlyMode) {
        log("Saved-only mode: trial-folder bottle discovery is skipped.", "warn");
      }
      if (requireSavedSinglesCurrent) {
        log(
          `Saved-analysis guard: all member bottles must be up-to-date (${THICKNESS_ANALYSIS_SCHEMA_VERSION}).`
        );
      }
    }

    if (!mountPoint) {
      log("NAS is not mounted. Mount the NAS and try again.", "warn");
      setSyncRunning(false);
      return;
    }
    if (!window.api?.runThicknessAnalysis) {
      log("Sync requires the desktop runtime.", "error");
      setSyncRunning(false);
      return;
    }
    if (!savedOnlyMode && (!window.api?.listNasDir || !window.api?.processThicknessBottle)) {
      log("Sync requires the desktop runtime.", "error");
      setSyncRunning(false);
      return;
    }
    if (recalcSavedAnalyses && !dryRun && !window.api?.movePath) {
      log("Saved-analysis recalc requires filesystem move support in desktop runtime.", "error");
      setSyncRunning(false);
      return;
    }
    const runtimeSchemaProbe = await ensureThicknessRuntimeSchemaCurrent({ onLog: log });
    if (!runtimeSchemaProbe.ok) {
      const message =
        runtimeSchemaProbe.message ||
        "Local thickness runtime schema check failed. Restart the app and retry.";
      log(message, "error");
      setSyncError(message);
      setSyncRunning(false);
      return;
    }
    if (runtimeSchemaProbe.singleSchemaVersion || runtimeSchemaProbe.multipleSchemaVersion) {
      log(
        `Runtime schema check: single=${runtimeSchemaProbe.singleSchemaVersion || "missing"}, multiple=${runtimeSchemaProbe.multipleSchemaVersion || "missing"}.`
      );
    }

    try {
      log("Loading existing thickness records...");
      const existing = await fetchThicknessIndex();
      const existingByKey = new Map<string, number>(
        existing.map((row) => [`${row.trial_name}/${row.relative_bottle_id}`, row.id])
      );
      const existingKeys = new Set<string>(existingByKey.keys());
      const existingMetadataByKey = new Map<string, ThicknessBottle>(
        metadata.map((row) => [`${row.trial_name}/${row.relative_bottle_id}`, row])
      );
      const selectedIdSet = selectedOverrideIds?.length
        ? new Set<number>(selectedOverrideIds)
        : selectedIds;
      const selectedRows = metadata.filter((row) => selectedIdSet.has(row.id));
      const selectedKeys = new Set<string>(
        selectedRows.map((row) => `${row.trial_name}/${row.relative_bottle_id}`)
      );
      const selectedTrials = Array.from(new Set(selectedRows.map((row) => row.trial_name))).sort();
      const selectedBottleIdsByTrial = selectedRows.reduce((acc, row) => {
        const existingSet = acc.get(row.trial_name) ?? new Set<number>();
        existingSet.add(row.relative_bottle_id);
        acc.set(row.trial_name, existingSet);
        return acc;
      }, new Map<string, Set<number>>());
      const timestampHintByKey = metadata.reduce((acc, row) => {
        if (typeof row.timestamp === "string" && row.timestamp.trim()) {
          acc.set(`${row.trial_name}/${row.relative_bottle_id}`, row.timestamp);
        }
        return acc;
      }, new Map<string, string>());
      log(`Database index loaded (${existingKeys.size} bottles).`);
      if (recalcExisting && recalcSelectedOnly && selectedKeys.size === 0) {
        log("Recalc selected-only requested, but no selected bottles were found.", "warn");
        return;
      }

      const rootPath = joinNasPath(mountPoint, NAS_THICKNESS_DIR);
      let folders: string[] = [];
      if (savedOnlyMode) {
        folders = [];
      } else if (recalcExisting && recalcSelectedOnly) {
        folders = selectedTrials;
        log(
          `Selected-only fast path: direct processing for ${folders.length} selected trial folder(s) under ${rootPath}.`
        );
      } else {
        log(`Scanning NAS root: ${rootPath}`);
        const rootListing = await window.api.listNasDir(rootPath);
        if (!rootListing.ok || !rootListing.entries) {
          throw new Error(rootListing.message || "Failed to list NAS thickness directory.");
        }

        folders = rootListing.entries
          .filter(
            (entry) =>
              entry.isDir &&
              entry.name !== "output" &&
              entry.name !== "_dry_run" &&
              entry.name !== "multiple_analyses"
          )
          .map((entry) => entry.name)
          .sort();
      }
      if (!syncControlRef.current.stop) {
        setSyncProgress((prev) => ({ ...prev, foldersTotal: folders.length }));
      }
      if (!folders.length && !savedOnlyMode) {
        log("No trial folders found in NAS thickness directory.", "warn");
      }

      const isDirectFileError = (message: string) =>
        /Expected at least .* CSV files|Folder not found|No data folder|No valid data frames produced|No CSV files found/i.test(
          message
        );

      const resolveDataDirForTrial = async (trialDir: string): Promise<string | null> => {
        const trialListing = await window.api!.listNasDir(trialDir);
        if (!trialListing.ok || !trialListing.entries) return null;
        const entries = trialListing.entries as NasEntryMeta[];
        const dataEntry = entries.find((entry) => entry.isDir && entry.name.toLowerCase() === "data");
        if (dataEntry) return joinNasPath(trialDir, dataEntry.name);

        const hasRootCsv = entries.some(
          (entry) => (entry.isFile || !entry.isDir) && entry.name.toLowerCase().endsWith(".csv")
        );
        return hasRootCsv ? trialDir : null;
      };

      const computeBottleFingerprint = async (
        dataDir: string | null,
        bottleId: number
      ): Promise<string | null> => {
        if (!dataDir || !window.api?.listNasDir) return null;
        const listing = await window.api.listNasDir(dataDir, {
          withStats: true,
          namePrefix: `bottle${bottleId}_`,
          nameSuffix: ".csv"
        });
        if (!listing.ok || !listing.entries) return null;
        return buildQuickBottleFingerprint(listing.entries as NasEntryMeta[], bottleId);
      };

      const isRecalcUpToDate = (
        key: string,
        sourceTimestamp: string | null | undefined,
        sourceFingerprint: string | null | undefined
      ) => {
        const existingRow = existingMetadataByKey.get(key);
        if (!existingRow) return false;

        const payload =
          existingRow.analysis_payload && typeof existingRow.analysis_payload === "object"
            ? (existingRow.analysis_payload as ThicknessAnalysisContract)
            : null;
        if (!payload) return false;

        const schemaVersion =
          typeof payload.schema_version === "string"
            ? payload.schema_version
            : typeof payload.analyzer_meta?.schema_version === "string"
              ? payload.analyzer_meta.schema_version
              : null;
        if (schemaVersion !== THICKNESS_ANALYSIS_SCHEMA_VERSION) return false;

        const payloadRawPointsRef =
          payload.raw_points_ref && typeof payload.raw_points_ref === "object"
            ? (payload.raw_points_ref as Record<string, unknown>)
            : null;
        const payloadFingerprint =
          payloadRawPointsRef && typeof payloadRawPointsRef.quick_fingerprint === "string"
            ? payloadRawPointsRef.quick_fingerprint
            : null;
        if (sourceFingerprint && payloadFingerprint) {
          return sourceFingerprint === payloadFingerprint;
        }

        const existingTs = timestampComparable(existingRow.timestamp);
        const sourceTs = timestampComparable(sourceTimestamp);
        return !!existingTs && !!sourceTs && existingTs === sourceTs;
      };

      const processBottleJob = async (
        folder: string,
        bottleId: number,
        trialDir: string,
        outputBase: string,
        sourceTimestamp: string | null | undefined,
        sourceFingerprint: string | null | undefined
      ) => {
        const bottleStartedAt = Date.now();
        const key = `${folder}/${bottleId}`;
        const existingId = existingByKey.get(key);
        try {
          const timestampHint = sourceTimestamp ?? timestampHintByKey.get(key);
          if (
            recalcExisting &&
            !recalcSelectedOnly &&
            existingId != null &&
            isRecalcUpToDate(key, timestampHint, sourceFingerprint)
          ) {
            log(
              `Skipping bottle ${bottleId} in ${folder}: already up-to-date ` +
                `(${THICKNESS_ANALYSIS_SCHEMA_VERSION}${
                  sourceFingerprint ? ", quick fingerprint match" : ", unchanged timestamp"
                }).`
            );
            return { ok: true as const };
          }
          log(`Processing bottle ${bottleId} in ${folder}...`);
          const result = await window.api!.processThicknessBottle({
            trialDir,
            bottleId,
            outputBase: outputBase,
            timestampHint,
            sourceFingerprint: sourceFingerprint || undefined
          });
          if (!result.ok || !result.result?.bottle) {
            throw new Error(result.message || "Thickness processing failed.");
          }

          const payload = normalizeBottlePayload(result.result.bottle, folder, bottleId);
          const producedSchemaVersion = getAnalysisSchemaVersion(
            payload.analysis_payload as ThicknessAnalysisContract | null | undefined
          );
          if (producedSchemaVersion !== THICKNESS_ANALYSIS_SCHEMA_VERSION) {
            const detectedSchema = producedSchemaVersion || "missing";
            throw new Error(
              `Runtime produced schema ${detectedSchema} (expected ${THICKNESS_ANALYSIS_SCHEMA_VERSION}). ` +
                "Aborting DB write to prevent stale analysis payload."
            );
          }
          if (dryRun) {
            if (recalcExisting && existingId != null) {
              log(`Dry run: prepared update payload for bottle ${bottleId} (id=${existingId}).`);
            } else {
              log(`Dry run: prepared payload for bottle ${bottleId}.`);
            }
          } else {
            const dbWriteStartedAt = Date.now();
            if (recalcExisting && existingId != null) {
              const updated = await updateThicknessBottle(existingId, payload);
              log(`Updated bottle ${updated.id} (${folder} / ${bottleId}).`, "success");
            } else {
              const created = await createThicknessBottle(payload);
              log(`Inserted bottle ${created.id} (${folder} / ${bottleId}).`, "success");
              existingByKey.set(key, created.id);
              existingKeys.add(key);
            }
            log(
              `Bottle ${bottleId} (${folder}): DB write finished (${((Date.now() - dbWriteStartedAt) / 1000).toFixed(1)}s).`
            );
          }

          const outputDir = joinNasPath(outputBase, "output");
          const outputCheckStartedAt = Date.now();
          void validateOutputFiles(outputDir, `bottle${bottleId}`)
            .then(() => {
              log(
                `Bottle ${bottleId} (${folder}): Output check finished (${((Date.now() - outputCheckStartedAt) / 1000).toFixed(1)}s).`
              );
            })
            .catch((err) => {
              const message = err instanceof Error ? err.message : "unknown output check error";
              log(`Bottle ${bottleId} (${folder}): Output check failed (${message}).`, "error");
            });

          if (dryRun) {
            const prior = lastDryRunDirRef.current;
            if (prior && prior !== outputBase) {
              await deleteDryRunDir(prior, "superseded by newer dry run");
            }
            lastDryRunDirRef.current = outputBase;
          }

          const bottleMs = Date.now() - bottleStartedAt;
          log(`Sync complete for bottle ${bottleId} in ${(bottleMs / 1000).toFixed(1)}s.`);
          return { ok: true as const };
        } catch (err) {
          const message = err instanceof Error ? err.message : "sync failed";
          log(`Sync failed for bottle ${bottleId}: ${message}`, "error");
          if (recalcExisting && existingId != null && !dryRun) {
            try {
              const recalcStatus: ThicknessRecalcStatus = {
                state: "failed",
                failed_at: new Date().toISOString(),
                target_schema_version: THICKNESS_ANALYSIS_SCHEMA_VERSION,
                error_message: message,
                trial_name: folder,
                relative_bottle_id: bottleId,
                source_fingerprint: sourceFingerprint || null
              };
              await markThicknessBottleRecalcFailed(existingId, recalcStatus);
              const existingRow = existingMetadataByKey.get(key);
              if (existingRow) {
                const basePayload =
                  existingRow.analysis_payload &&
                  typeof existingRow.analysis_payload.schema_version === "string" &&
                  existingRow.analysis_payload.schema_version.trim()
                    ? existingRow.analysis_payload
                    : {
                        ...(existingRow.analysis_payload || {}),
                        schema_version: "thickness_analysis.failed"
                      };
                existingMetadataByKey.set(key, {
                  ...existingRow,
                  analysis_payload: {
                    ...basePayload,
                    recalc_status: recalcStatus
                  }
                });
              }
              log(`Persisted failed recalc marker for bottle ${existingId} (${folder} / ${bottleId}).`, "warn");
            } catch (markErr) {
              const markMessage = markErr instanceof Error ? markErr.message : "unknown DB marker error";
              log(
                `Failed to persist recalc failure marker for bottle ${bottleId}: ${markMessage}`,
                "error"
              );
            }
          }
          return { ok: false as const, message };
        } finally {
          if (!syncControlRef.current.stop) {
            setSyncProgress((prev) => ({
              ...prev,
              filesDone: Math.min(prev.filesDone + 1, prev.filesTotal),
              totalFilesDone: prev.totalFilesDone + 1
            }));
          }
        }
      };

      let stoppedEarly = false;
      for (const folder of folders) {
        if (!(await waitForSyncReady())) {
          stoppedEarly = true;
          break;
        }
        const trialDir = joinNasPath(rootPath, folder);
        const inSelectedOnlyMode = recalcExisting && recalcSelectedOnly;
        const selectedIdsForTrial = Array.from(selectedBottleIdsByTrial.get(folder) ?? new Set<number>()).sort(
          (a, b) => a - b
        );
        let fallbackSelectedIds: number[] = [];

        if (inSelectedOnlyMode) {
          if (!selectedIdsForTrial.length) {
            log(`No selected bottles from current table selection found in ${folder}.`, "warn");
            if (!syncControlRef.current.stop) {
              setSyncProgress((prev) => ({
                ...prev,
                foldersDone: Math.min(prev.foldersDone + 1, prev.foldersTotal),
                filesTotal: 0,
                filesDone: 0
              }));
            }
            continue;
          }

          const directDataDir = await resolveDataDirForTrial(trialDir);
          if (!directDataDir) {
            log(`Selected-only fingerprint probe: no data directory found in ${folder}.`, "warn");
          }

          log(
            `Selected-only direct mode: processing ${selectedIdsForTrial.length} selected bottle(s) in ${folder} without trial CSV scan.`
          );
          if (!syncControlRef.current.stop) {
            setSyncProgress((prev) => ({
              ...prev,
              currentFolder: folder,
              filesTotal: selectedIdsForTrial.length,
              filesDone: 0,
              totalFiles: prev.totalFiles + selectedIdsForTrial.length
            }));
          }

          for (const bottleId of selectedIdsForTrial) {
            if (!(await waitForSyncReady())) {
              stoppedEarly = true;
              break;
            }
            const outputBase = dryRun
              ? joinNasPath(mountPoint, NAS_THICKNESS_DRY_RUN_DIR, folder, `bottle${bottleId}`)
              : trialDir;
            const sourceFingerprint = await computeBottleFingerprint(directDataDir, bottleId);
            const result = await processBottleJob(
              folder,
              bottleId,
              trialDir,
              outputBase,
              timestampHintByKey.get(`${folder}/${bottleId}`),
              sourceFingerprint
            );
            if (!result.ok && result.message && isDirectFileError(result.message)) {
              fallbackSelectedIds.push(bottleId);
            }
          }

          if (stoppedEarly) break;

          if (!fallbackSelectedIds.length) {
            if (!syncControlRef.current.stop) {
              setSyncProgress((prev) => ({
                ...prev,
                foldersDone: Math.min(prev.foldersDone + 1, prev.foldersTotal),
                filesTotal: 0,
                filesDone: 0
              }));
            }
            continue;
          }

          log(
            `Selected-only fallback: retrying ${fallbackSelectedIds.length} bottle(s) in ${folder} via CSV discovery.`,
            "warn"
          );
        }

        log(`Scanning trial ${folder}...`);

        const trialListing = await window.api.listNasDir(trialDir);
        if (!trialListing.ok || !trialListing.entries) {
          log(`Failed to list ${folder}: ${trialListing.message || "unknown error"}`, "error");
          if (!syncControlRef.current.stop) {
            setSyncProgress((prev) => ({
              ...prev,
              foldersDone: Math.min(prev.foldersDone + 1, prev.foldersTotal),
              filesTotal: 0,
              filesDone: 0
            }));
          }
          continue;
        }

        const dataEntry = trialListing.entries.find(
          (entry) => entry.isDir && entry.name.toLowerCase() === "data"
        );
        const isCsvEntry = (entry: { name: string; isFile: boolean; isDir: boolean }) =>
          entry.name.toLowerCase().endsWith(".csv");
        const isFileLike = (entry: { name: string; isFile: boolean; isDir: boolean }) =>
          entry.isFile || !entry.isDir;
        let dataDir = "";
        let dataEntries = trialListing.entries;

        if (dataEntry) {
          dataDir = joinNasPath(trialDir, dataEntry.name);
          const dataListing = await window.api.listNasDir(dataDir);
          if (!dataListing.ok || !dataListing.entries) {
            log(
              `Failed to list ${folder}/${dataEntry.name}: ${dataListing.message || "unknown error"}`,
              "error"
            );
            if (!syncControlRef.current.stop) {
              setSyncProgress((prev) => ({
                ...prev,
                foldersDone: Math.min(prev.foldersDone + 1, prev.foldersTotal),
                filesTotal: 0,
                filesDone: 0
              }));
            }
            continue;
          }
          dataEntries = dataListing.entries;
        } else {
          const rootCsvFiles = trialListing.entries.filter(
            (entry) => isFileLike(entry) && isCsvEntry(entry)
          );
          if (rootCsvFiles.length) {
            dataDir = trialDir;
            dataEntries = trialListing.entries;
            log(`No data/ folder found in ${folder}; using trial root for CSV files.`, "warn");
          } else {
            log(`No data folder or CSV files found in ${folder}.`, "warn");
            if (!syncControlRef.current.stop) {
              setSyncProgress((prev) => ({
                ...prev,
                foldersDone: Math.min(prev.foldersDone + 1, prev.foldersTotal),
                filesTotal: 0,
                filesDone: 0
              }));
            }
            continue;
          }
        }

        const csvFiles = dataEntries
          .filter((entry) => isFileLike(entry) && isCsvEntry(entry))
          .map((entry) => entry.name);
        if (!csvFiles.length) {
          log(
            `No CSV files found in ${folder}${dataDir === trialDir ? "" : "/data"}.`,
            "warn"
          );
          if (!syncControlRef.current.stop) {
            setSyncProgress((prev) => ({
              ...prev,
              foldersDone: Math.min(prev.foldersDone + 1, prev.foldersTotal),
              filesTotal: 0,
              filesDone: 0
            }));
          }
          continue;
        }

        let expectedSlices: number | null = null;
        const findSettingsEntry = (entries: { name: string; isFile: boolean; isDir: boolean }[]) =>
          entries.find(
            (entry) =>
              isFileLike(entry) && entry.name.toLowerCase() === "settings.txt"
          ) ||
          entries.find(
            (entry) =>
              isFileLike(entry) &&
              entry.name.toLowerCase().startsWith("settings") &&
              entry.name.toLowerCase().endsWith(".txt")
          );

        let settingsEntry = findSettingsEntry(trialListing.entries);
        let settingsDir = trialDir;
        if (!settingsEntry && dataDir && dataDir !== trialDir) {
          settingsEntry = findSettingsEntry(dataEntries);
          settingsDir = dataDir;
        }

        if (settingsEntry) {
          if (!window.api?.readTextFile) {
            log("Settings validation skipped: file reading requires the desktop runtime.", "warn");
          } else {
            const settingsPath = joinNasPath(settingsDir, settingsEntry.name);
            const settingsRead = await window.api.readTextFile(settingsPath);
            if (settingsRead.ok && typeof settingsRead.text === "string") {
              expectedSlices = parseSliceCount(settingsRead.text);
              if (expectedSlices) {
                log(`Detected ${expectedSlices} slices from ${settingsEntry.name}.`);
              } else {
                log(
                  `Unable to parse slice count in ${settingsEntry.name}; skipping slice validation.`,
                  "warn"
                );
              }
            } else {
              log(
                `Failed to read ${settingsEntry.name}: ${settingsRead.message || "unknown error"}`,
                "warn"
              );
            }
          }
        } else {
          log("No settings file found; skipping slice-count validation.", "warn");
        }

        const selectedIdsForFolder = inSelectedOnlyMode
          ? (fallbackSelectedIds.length ? fallbackSelectedIds : selectedIdsForTrial)
          : [];
        const selectedIdFilter = new Set<number>(selectedIdsForFolder);

        const bottleIdSet = new Set<number>();
        const availableSelectedIds = new Set<number>();
        const sliceCountByBottle = new Map<number, Set<number>>();
        const firstCsvFileByBottle = new Map<number, string>();
        csvFiles.forEach((file) => {
          const match = /^bottle[^0-9]*([0-9]+)/i.exec(file);
          if (!match) return;
          const id = Number(match[1]);
          if (!Number.isFinite(id)) return;

          if (inSelectedOnlyMode) {
            if (!selectedIdFilter.has(id)) return;
            availableSelectedIds.add(id);
          }

          bottleIdSet.add(id);
          const previousFirstFile = firstCsvFileByBottle.get(id);
          if (!previousFirstFile || file.localeCompare(previousFirstFile) < 0) {
            firstCsvFileByBottle.set(id, file);
          }
          const sliceMatch = /slice[^0-9]*([0-9]+)/i.exec(file);
          if (!sliceMatch) return;
          const sliceId = Number(sliceMatch[1]);
          if (!Number.isFinite(sliceId)) return;
          const set = sliceCountByBottle.get(id) || new Set<number>();
          set.add(sliceId);
          sliceCountByBottle.set(id, set);
        });

        const bottleIds = inSelectedOnlyMode
          ? selectedIdsForFolder
          : Array.from(bottleIdSet.values()).sort((a, b) => a - b);

        if (!bottleIds.length) {
          if (inSelectedOnlyMode) {
            log(`No selected bottles from current table selection found in ${folder}.`, "warn");
          } else {
            const sample = csvFiles.slice(0, 5).join(", ");
            log(
              `No bottle ids detected in ${folder}${dataDir === trialDir ? "" : "/data"}.` +
                (sample ? ` Example files: ${sample}` : ""),
              "warn"
            );
          }
          if (!syncControlRef.current.stop) {
            setSyncProgress((prev) => ({
              ...prev,
              foldersDone: Math.min(prev.foldersDone + 1, prev.foldersTotal),
              filesTotal: 0,
              filesDone: 0
            }));
          }
          continue;
        }

        const missingSelectedIds = inSelectedOnlyMode
          ? bottleIds.filter((id) => !availableSelectedIds.has(id))
          : [];

        if (inSelectedOnlyMode && missingSelectedIds.length) {
          const sample = missingSelectedIds.slice(0, 5).map((id) => `bottle${id}`).join(", ");
          log(
            `Skipping ${missingSelectedIds.length} selected bottle(s) in ${folder} because no CSV files were found.` +
              (sample ? ` Example: ${sample}` : ""),
            "warn"
          );
        }

        const bottleIdsWithFiles = inSelectedOnlyMode
          ? bottleIds.filter((id) => availableSelectedIds.has(id))
          : bottleIds;

        const validBottleIds = expectedSlices
          ? bottleIdsWithFiles.filter(
              (id) => (sliceCountByBottle.get(id)?.size ?? 0) >= expectedSlices
            )
          : bottleIdsWithFiles;
        const incompleteIds = expectedSlices
          ? bottleIdsWithFiles.filter(
              (id) => (sliceCountByBottle.get(id)?.size ?? 0) < expectedSlices
            )
          : [];

        if (expectedSlices && incompleteIds.length) {
          const sample = incompleteIds
            .slice(0, 5)
            .map((id) => `bottle${id} (${sliceCountByBottle.get(id)?.size ?? 0}/${expectedSlices})`)
            .join(", ");
          log(
            `Skipping ${incompleteIds.length} bottle(s) in ${folder} due to missing slices (${expectedSlices} required).` +
              (sample ? ` Example: ${sample}` : ""),
            "warn"
          );
        }

        if (!validBottleIds.length) {
          log(`No valid bottles to sync in ${folder} after slice validation.`, "warn");
          if (!syncControlRef.current.stop) {
            setSyncProgress((prev) => ({
              ...prev,
              foldersDone: Math.min(prev.foldersDone + 1, prev.foldersTotal),
              filesTotal: 0,
              filesDone: 0
            }));
          }
          continue;
        }

        let targetCandidateIds = validBottleIds;
        if (recalcExisting && recalcSelectedOnly && !targetCandidateIds.length) {
          log(`No selected bottles to recalculate in ${folder}.`, "warn");
        }

        const skippedIds = targetCandidateIds.filter((id) =>
          existingKeys.has(`${folder}/${id}`)
        );
        const newIds = targetCandidateIds.filter((id) => !existingKeys.has(`${folder}/${id}`));

        if (dryRun) {
          if (!recalcExisting && skippedIds.length) {
            const sample = skippedIds.slice(0, 3).join(", ");
            log(
              `Filter check: ${skippedIds.length} existing bottle(s) skipped in ${folder}.` +
                (sample ? ` Example: ${sample}` : ""),
              "warn"
            );
          }
          if (recalcExisting) {
            log(
              `Recalc check: ${targetCandidateIds.length} bottle(s) in ${folder} (` +
                `${skippedIds.length} existing / ${newIds.length} new).`
            );
          } else {
            const overlap = newIds.filter((id) => existingKeys.has(`${folder}/${id}`));
            if (overlap.length) {
              log(`Filter check FAILED: ${overlap.length} bottle(s) already in DB.`, "error");
            } else {
              log("Filter check: OK (trial_name + relative_bottle_id).", "success");
            }
          }
        }

        const targetIds = recalcExisting ? targetCandidateIds : newIds;

        if (!syncControlRef.current.stop) {
          setSyncProgress((prev) => ({
            ...prev,
            currentFolder: folder,
            filesTotal: targetIds.length,
            filesDone: 0,
            totalFiles: prev.totalFiles + targetIds.length
          }));
        }

        if (!targetIds.length) {
          log(
            recalcExisting
              ? `No bottles to recalculate in ${folder}.`
              : `No new bottles to sync in ${folder}.`,
            "warn"
          );
          if (!syncControlRef.current.stop) {
            setSyncProgress((prev) => ({
              ...prev,
              foldersDone: Math.min(prev.foldersDone + 1, prev.foldersTotal),
              filesTotal: 0,
              filesDone: 0
            }));
          }
          continue;
        }

        for (const bottleId of targetIds) {
          if (!(await waitForSyncReady())) {
            stoppedEarly = true;
            break;
          }
          const outputBase = dryRun
            ? joinNasPath(mountPoint, NAS_THICKNESS_DRY_RUN_DIR, folder, `bottle${bottleId}`)
            : trialDir;
          const discoveredTimestamp = timestampFromCsvFilename(firstCsvFileByBottle.get(bottleId) || "");
          const sourceFingerprint = inSelectedOnlyMode
            ? await computeBottleFingerprint(dataDir, bottleId)
            : null;
          await processBottleJob(
            folder,
            bottleId,
            trialDir,
            outputBase,
            discoveredTimestamp ?? timestampHintByKey.get(`${folder}/${bottleId}`),
            sourceFingerprint
          );
        }

        if (!stoppedEarly) {
          if (!syncControlRef.current.stop) {
            setSyncProgress((prev) => ({
              ...prev,
              foldersDone: Math.min(prev.foldersDone + 1, prev.foldersTotal),
              filesTotal: 0,
              filesDone: 0
            }));
          }
        }
        if (stoppedEarly) break;
      }

      if (!stoppedEarly && !syncControlRef.current.stop && recalcSavedAnalyses) {
        log("Refreshing thickness metadata for saved-analysis recalc...");
        clearCache();
        const metadataSnapshot = await fetchThicknessMetadata();
        const metadataById = new Map<number, ThicknessBottle>(
          metadataSnapshot.map((row) => [row.id, row])
        );

        log("Loading saved multiple analyses...");
        clearCache();
        const savedAnalysisRows = await loadThicknessMultipleAnalyses();
        if (!savedAnalysisRows.length) {
          log("No saved multiple analyses found for recalc.", "warn");
        }

        for (const savedAnalysis of savedAnalysisRows) {
          if (!(await waitForSyncReady())) {
            stoppedEarly = true;
            break;
          }
          const startedAt = Date.now();
          try {
            const bottleIds = Array.from(
              new Set(
                (savedAnalysis.bottle_id_array || [])
                  .map((id) => Number(id))
                  .filter((id) => Number.isFinite(id))
              )
            );
            if (!bottleIds.length) {
              log(
                `Saved analysis ${savedAnalysis.id} (${savedAnalysis.name}): skipped (empty bottle_id_array).`,
                "warn"
              );
              continue;
            }

            const missingBottleIds = bottleIds.filter((id) => !metadataById.has(id));
            if (missingBottleIds.length) {
              const sample = missingBottleIds.slice(0, 5).join(", ");
              log(
                `Saved analysis ${savedAnalysis.id} (${savedAnalysis.name}): skipped (${missingBottleIds.length} missing bottle row(s): ${sample}${
                  missingBottleIds.length > 5 ? ", ..." : ""
                }).`,
                "warn"
              );
              continue;
            }

            if (requireSavedSinglesCurrent) {
              const heavyById = await hydrateBottleHeavyFields(bottleIds);
              if (heavyById.size) {
                bottleIds.forEach((id) => {
                  const base = metadataById.get(id);
                  const heavy = heavyById.get(id);
                  if (!base || !heavy) return;
                  metadataById.set(id, { ...base, ...heavy });
                });
              }
              const outdatedBottleIds = bottleIds.filter((id) => !isBottleRecalcCurrent(metadataById.get(id)));
              if (outdatedBottleIds.length) {
                const sample = outdatedBottleIds.slice(0, 5).join(", ");
                log(
                  `Saved analysis ${savedAnalysis.id} (${savedAnalysis.name}): skipped (${outdatedBottleIds.length} bottle(s) not up-to-date for ${THICKNESS_ANALYSIS_SCHEMA_VERSION}: ${sample}${
                    outdatedBottleIds.length > 5 ? ", ..." : ""
                  }).`,
                  "warn"
                );
                continue;
              }
            }

            log(
              `Recalculating saved analysis ${savedAnalysis.id} (${savedAnalysis.name}) with ${bottleIds.length} bottle(s)...`
            );
            const dataMap = await fetchThicknessData(bottleIds);
            const hasAnyData = bottleIds.some((id) => {
              const rows = dataMap[id];
              return Array.isArray(rows) && rows.length > 0;
            });
            if (!hasAnyData) {
              log(
                `Saved analysis ${savedAnalysis.id} (${savedAnalysis.name}): skipped (no thickness rows).`,
                "warn"
              );
              continue;
            }

            const selectedBottles = bottleIds
              .map((id) => metadataById.get(id))
              .filter((row): row is ThicknessBottle => !!row);
            const analysisInput = buildThicknessPayload(selectedBottles, bottleIds, dataMap);
            const analysisResponse = await window.api.runThicknessAnalysis(analysisInput);
            if (!analysisResponse?.ok || !analysisResponse.outputDir || !analysisResponse.outputStub) {
              throw new Error(analysisResponse?.message || "Thickness analysis failed.");
            }
            if (!analysisResponse.analysis || typeof analysisResponse.analysis !== "object") {
              throw new Error("Saved-analysis recalc failed: missing analysis payload.");
            }

            const multiplePayload = buildMultipleAnalysisPayload(
              analysisResponse.analysis as Record<string, unknown>,
              savedAnalysis.name,
              bottleIds
            );

            if (dryRun) {
              log(
                `Dry run: prepared update payload for saved analysis ${savedAnalysis.id} (${savedAnalysis.name}).`
              );
              continue;
            }

            await updateThicknessMultipleAnalysis(savedAnalysis.id, multiplePayload);

            const outputDir = joinNasPath(
              mountPoint,
              NAS_THICKNESS_MULTIPLE_DIR,
              buildMultipleAnalysisFolderName(savedAnalysis.id, savedAnalysis.name)
            );
            if (window.api?.deletePath) {
              const deleteExisting = await window.api.deletePath(outputDir);
              if (!deleteExisting.ok) {
                log(
                  `Saved analysis ${savedAnalysis.id} (${savedAnalysis.name}): output cleanup note: ${deleteExisting.message || "delete skipped"}.`,
                  "warn"
                );
              }
            }

            const moveOutput = await window.api.movePath(analysisResponse.outputDir, outputDir);
            if (!moveOutput.ok) {
              throw new Error(
                moveOutput.message ||
                  `Failed to move recalc output to ${outputDir}.`
              );
            }

            const outputStub = analysisResponse.outputStub || THICKNESS_ANALYSIS_STUB;
            const tmpRoot = deriveTmpRoot(analysisResponse.outputDir);
            const movedAnalysisJson = await window.api.movePath(
              `${tmpRoot}/${outputStub}.json`,
              joinNasPath(outputDir, `${outputStub}.json`)
            );
            const movedPayloadJson = await window.api.movePath(
              `${tmpRoot}/${outputStub}_payload.json`,
              joinNasPath(outputDir, `${outputStub}_payload.json`)
            );
            if (!movedAnalysisJson.ok || !movedPayloadJson.ok) {
              log(
                `Saved analysis ${savedAnalysis.id} (${savedAnalysis.name}): output moved, but metadata json move was incomplete.`,
                "warn"
              );
            }

            log(
              `Saved analysis ${savedAnalysis.id} (${savedAnalysis.name}) recalculated in ${((Date.now() - startedAt) / 1000).toFixed(1)}s.`,
              "success"
            );
          } catch (err) {
            const message = err instanceof Error ? err.message : "saved analysis recalc failed";
            log(
              `Saved analysis ${savedAnalysis.id} (${savedAnalysis.name}) failed: ${message}`,
              "error"
            );
          }
        }

        clearCache();
        await refreshThicknessAnalyses();
      }

      clearCache();
      await loadMetadata();
      setAnalysisWarning(null);
      setPendingRecalcPrompt(null);
      setGalleryCacheBustKey(Date.now());
      if (!stoppedEarly && !syncControlRef.current.stop) {
        log("Sync complete.", "success");
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Sync failed";
      if (!syncControlRef.current.stop) {
        setSyncError(message);
        log(`Error: ${message}`, "error");
      }
    } finally {
      const wasStopped = syncControlRef.current.stop;
      setSyncRunning(false);
      setSyncPaused(false);
      setSyncStopRequested(false);
      setSyncSelectedBottleOverrideIds(null);
      syncControlRef.current = { pause: false, stop: false };
      setSyncProgress((prev) => ({ ...prev, currentFolder: "" }));
      if (wasStopped) {
        appendSyncLog("Sync stopped.", true, "warn", true);
      }
      syncLogSuppressedRef.current = false;
    }
  };

  const setPreflightProgress = (label: string, percent: number, detail?: string) => {
    setAnalysisStatusMessage(label);
    setAnalysisProgress({ label, percent: clampPercent(percent), detail });
  };

  const stopAnalysisBusyState = () => {
    setAnalysisPhase("idle");
    setAnalysisStatusMessage(null);
    setAnalysisProgress(null);
    analyzeStartRef.current = null;
  };

  const runThicknessAnalysisForIds = async (ids: number[], runId: number) => {
    setPreflightProgress("Checking local analysis runtime...", 68, `${ids.length} bottle(s)`);
    let runtimeSchemaProbe: Awaited<ReturnType<typeof ensureThicknessRuntimeSchemaCurrent>>;
    try {
      runtimeSchemaProbe = await ensureThicknessRuntimeSchemaCurrent();
    } catch (err) {
      if (analyzeRunRef.current !== runId) return;
      stopAnalysisBusyState();
      setAnalysisError(
        err instanceof Error
          ? err.message
          : "Local thickness runtime schema check failed. Restart the app and retry."
      );
      return;
    }
    if (analyzeRunRef.current !== runId) return;
    if (!runtimeSchemaProbe.ok) {
      stopAnalysisBusyState();
      setAnalysisError(
        runtimeSchemaProbe.message ||
          "Local thickness runtime schema check failed. Restart the app and retry."
      );
      return;
    }
    if (runtimeSchemaProbe.refreshed) {
      setAnalysisWarning("Local Python runtime was refreshed from wheelhouse. Continuing with analysis.");
    }

    analyzeStartRef.current = Date.now();
    setAnalysisPhase("running");
    setAnalysisStatusMessage("Averaging selected bottles and rendering plots...");
    setAnalysisProgress(null);

    try {
      const dataMap = await fetchThicknessData(ids);
      if (analyzeRunRef.current !== runId) return;
      const hasAnyData = ids.some((id) => {
        const rows = dataMap[id];
        return Array.isArray(rows) && rows.length > 0;
      });
      if (!hasAnyData) {
        const missingDataIds = ids.filter((id) => {
          const rows = dataMap[id];
          return !Array.isArray(rows) || rows.length === 0;
        });
        const message = missingDataIds.length
          ? `No thickness data found for the selected samples (${missingDataIds.length}/${ids.length} selected bottles have no raw data).`
          : "No thickness data found for the selected samples.";
        setAnalysisError(message);
        setPendingRecalcPrompt({
          kind: "raw-data",
          title: "Raw Data Missing",
          message: missingDataIds.length
            ? `The selected bottles include missing raw thickness data (${missingDataIds.length}/${ids.length}). Recalc these selected bottles now?`
            : "No analyzable raw thickness points were found for the current selection. Recalc selected bottles now?",
          bottleIds: missingDataIds.length ? missingDataIds : ids,
          totalSelected: ids.length
        });
        return;
      }

      const meanById = await hydrateBottleMeanProfiles(ids);
      if (analyzeRunRef.current !== runId) return;
      const metadataById = new Map(metadata.map((row) => [row.id, row]));
      const selectedBottles = ids
        .map((id) => {
          const base = metadataById.get(id);
          if (!base) return null;
          const mean = meanById.get(id);
          return mean ? { ...base, ...mean } : base;
        })
        .filter((row): row is ThicknessBottle => !!row);
      if (!selectedBottles.length) {
        throw new Error("Unable to resolve selected bottle metadata.");
      }
      const payload = buildThicknessPayload(selectedBottles, ids, dataMap);
      if (!window.api?.runThicknessAnalysis) {
        throw new Error("Thickness analysis requires the desktop runtime.");
      }
      const response = await window.api.runThicknessAnalysis(payload);
      if (analyzeRunRef.current !== runId) return;
      if (!response?.ok || !response.outputDir || !response.outputStub) {
        throw new Error(response?.message || "Thickness analysis failed.");
      }
      if (analyzeRunRef.current !== runId) return;
      setAnalysisResult({ dir: response.outputDir, stub: response.outputStub, runId });
      setAnalysisPayload(
        response.analysis && typeof response.analysis === "object"
          ? (response.analysis as Record<string, unknown>)
          : null
      );
      setAnalysisBottleIds(ids);
      setAnalysisSaveError(null);
      if (analyzeStartRef.current) {
        setAnalysisDurationMs(Date.now() - analyzeStartRef.current);
      }
    } catch (err) {
      if (analyzeRunRef.current !== runId) return;
      console.error(err);
      setAnalysisPayload(null);
      setAnalysisBundleHydrating(false);
      setAnalysisBottleIds([]);
      setAnalysisError(err instanceof Error ? err.message : "Thickness analysis failed");
    } finally {
      if (analyzeRunRef.current === runId) {
        stopAnalysisBusyState();
      }
    }
  };

  const prepareAnalyzeRun = (ids: number[], runId: number) => {
    analyzeRunRef.current = runId;
    analyzeStartRef.current = null;
    setAnalysisPhase("preflight");
    setPreflightProgress("Checking selected bottles...", 12, `${ids.length} selected`);
    setAnalysisTriggered(true);
    setAnalysisError(null);
    setAnalysisWarning(null);
    setAnalysisDurationMs(null);
    setAnalysisSaveError(null);
    setAnalysisExportError(null);
    setAnalysisResult(null);
    setAnalysisPayload(null);
    setAnalysisBottleIds([]);
    setAnalysisBundleHydrating(false);
    setPendingRecalcPrompt(null);
    setSchemaRecalcChoices({});
  };

  const showSchemaRecalcPrompt = (prompt: PendingRecalcPrompt) => {
    const failedSet = new Set(prompt.failedBottleIds ?? []);
    const defaultChoices = prompt.bottleIds.reduce<Record<number, RecalcChoice>>((acc, id) => {
      acc[id] = failedSet.has(id) ? "exclude" : "recalc";
      return acc;
    }, {});
    setSchemaRecalcChoices(defaultChoices);
    setPendingRecalcPrompt(prompt);
  };

  const handleAnalyze = async () => {
    if (analysisBusy) return;
    if (selectedIds.size === 0) {
      setAnalysisError("Select at least one sample, then click Analyze.");
      return;
    }

    const ids = Array.from(selectedIds);
    const runId = analyzeRunRef.current + 1;
    prepareAnalyzeRun(ids, runId);

    if (ids.length === 1) {
      const selectedBottle = metadata.find((row) => row.id === ids[0]);
      if (!selectedBottle) {
        stopAnalysisBusyState();
        setAnalysisError("Unable to resolve selected bottle metadata.");
        setAnalysisResult(null);
        setAnalysisPayload(null);
        setAnalysisBottleIds([]);
        return;
      }
      if (!mountPoint) {
        stopAnalysisBusyState();
        setAnalysisError("Mount the NAS to view thickness outputs for the selected bottle.");
        setAnalysisResult(null);
        setAnalysisPayload(null);
        setAnalysisBottleIds(ids);
        return;
      }
      try {
        setPreflightProgress("Checking selected bottle version...", 55, "1 selected");
        const analysisStateById = await fetchThicknessBottleAnalysisStates(ids);
        if (analyzeRunRef.current !== runId) return;
        const state = analysisStateById[ids[0]];
        const schemaVersion = state?.schemaVersion ?? null;
        const recalcStatus = state?.recalcStatus ?? null;
        if (recalcStatus || schemaVersion !== THICKNESS_ANALYSIS_SCHEMA_VERSION) {
          const schemaById = { [ids[0]]: schemaVersion };
          const recalcStatusById = recalcStatus ? { [ids[0]]: recalcStatus } : undefined;
          setAnalysisWarning(
            recalcStatus
              ? "The selected bottle failed its last thickness recalc and should be excluded until recalc succeeds."
              : `The selected bottle is not on the latest thickness schema (${THICKNESS_ANALYSIS_SCHEMA_VERSION}). Recalc is recommended before analysis.`
          );
          showSchemaRecalcPrompt({
            kind: "schema",
            title: "Outdated Analysis Data",
            message: recalcStatus
              ? `Bottle ${ids[0]} failed its last recalc: ${recalcStatus.error_message}`
              : `Bottle ${ids[0]} is not on ${THICKNESS_ANALYSIS_SCHEMA_VERSION} (detected: ${schemaVersion || "missing"}).`,
            bottleIds: ids,
            totalSelected: ids.length,
            schemaById,
            failedBottleIds: recalcStatus ? ids : [],
            recalcStatusById,
            remainingBottleIds: []
          });
        }
      } catch (err) {
        console.warn("Schema-version check failed for single-bottle analysis", err);
        if (analyzeRunRef.current !== runId) return;
      }
      stopAnalysisBusyState();
      handleViewDetail(selectedBottle);
      return;
    }

    let schemaById: Record<number, string | null>;
    let recalcStatusById: Record<number, ThicknessRecalcStatus>;
    try {
      setPreflightProgress("Checking selected bottle versions...", 42, `${ids.length} selected`);
      const analysisStateById = await fetchThicknessBottleAnalysisStates(ids);
      schemaById = {};
      recalcStatusById = {};
      ids.forEach((id) => {
        const state = analysisStateById[id];
        schemaById[id] = state?.schemaVersion ?? null;
        if (state?.recalcStatus?.state === "failed") {
          recalcStatusById[id] = state.recalcStatus;
        }
      });
    } catch (err) {
      console.warn("Schema-version check failed for multi-bottle analysis", err);
      if (analyzeRunRef.current !== runId) return;
      stopAnalysisBusyState();
      setAnalysisError("Failed to verify selected bottle schema versions. Retry before analysis.");
      return;
    }
    if (analyzeRunRef.current !== runId) return;

    const failedBottleIds = ids.filter((id) => recalcStatusById[id]?.state === "failed");
    const failedBottleSet = new Set(failedBottleIds);
    const outdatedBottleIds = ids.filter(
      (id) =>
        !failedBottleSet.has(id) &&
        (schemaById[id] ?? null) !== THICKNESS_ANALYSIS_SCHEMA_VERSION
    );
    const blockedBottleIds = [...failedBottleIds, ...outdatedBottleIds];
    if (blockedBottleIds.length) {
      const warning = buildOutdatedSchemaMessage(blockedBottleIds, ids.length, schemaById);
      const blockedSet = new Set(blockedBottleIds);
      const remainingBottleIds = ids.filter((id) => !blockedSet.has(id));
      const failedHint = failedBottleIds.length
        ? `${failedBottleIds.length} selected bottle(s) failed their last recalc. `
        : "";
      setAnalysisWarning(`${failedHint}${warning} Recalc is required before analysis.`);
      stopAnalysisBusyState();
      showSchemaRecalcPrompt({
        kind: "schema",
        title: "Outdated Analysis Data",
        message: `${failedHint}${warning}`,
        bottleIds: blockedBottleIds,
        totalSelected: ids.length,
        schemaById,
        failedBottleIds,
        recalcStatusById,
        remainingBottleIds
      });
      return;
    }

    setPreflightProgress("Selected bottles are current.", 58, `${ids.length} bottle(s)`);
    await runThicknessAnalysisForIds(ids, runId);
  };

  const handleOpenRecalcFromPrompt = () => {
    const targetIds = schemaRecalcPrompt ? schemaPromptRecalcIds : pendingRecalcPrompt?.bottleIds || [];
    if (!targetIds.length) return;
    setPendingRecalcPrompt(null);
    setSchemaRecalcChoices({});
    openSyncWithSelectedRecalcPreset(targetIds);
  };

  const handleExcludeOutdatedAndAnalyzeRemaining = async () => {
    if (!schemaRecalcPrompt?.remainingBottleIds?.length || analysisBusy) return;
    const remainingIds = schemaRecalcPrompt.remainingBottleIds;
    const excludedCount = schemaRecalcPrompt.bottleIds.length;
    const runId = analyzeRunRef.current + 1;
    setSelectedIds(new Set(remainingIds));
    setGalleryPage(0);
    prepareAnalyzeRun(remainingIds, runId);
    setAnalysisWarning(
      `Excluded ${excludedCount} failed or outdated bottle(s). Continuing with ${remainingIds.length} current bottle(s).`
    );
    setPreflightProgress("Preparing analysis without outdated bottles...", 58, `${remainingIds.length} remaining`);
    await runThicknessAnalysisForIds(remainingIds, runId);
  };

  const handleSetSchemaRecalcChoice = (id: number, choice: RecalcChoice) => {
    setSchemaRecalcChoices((prev) => ({ ...prev, [id]: choice }));
  };

  const openZoom = (src: string, alt: string) => {
    setZoomSrc(src);
    setZoomAlt(alt);
  };

  const renderImageCard = (
    title: string,
    src: string,
    alt: string,
    accent: string,
    frameHeight = "clamp(320px, 26vw, 420px)"
  ) => (
    <div
      className="card"
      style={{
        background: "var(--surface-elevated)",
        borderColor: "#1f334d"
      }}
    >
      <div
        className="label"
        style={{
          textTransform: "uppercase",
          letterSpacing: 0.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            aria-hidden="true"
            style={{
              width: 8,
              height: 8,
              borderRadius: 999,
              background: accent,
              boxShadow: `0 0 10px ${accent}`
            }}
          />
          {title}
        </span>
        {src && (
          <span style={{ fontSize: 11, color: "var(--text-faint)", textTransform: "none" }}>
            Click to zoom
          </span>
        )}
      </div>
      <div
        style={{
          display: "grid",
          placeItems: "center",
          marginTop: 10,
          padding: 12,
          background: "#0c1422",
          borderRadius: 12,
          border: "1px solid #1e2b3d",
          boxShadow: "0 12px 26px rgba(0,0,0,0.35)",
          minHeight: 240,
          height: frameHeight,
          overflow: "hidden"
        }}
      >
        {src ? (
          <img
            src={src}
            alt={alt}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              borderRadius: 10,
              border: "1px solid #243752",
              background: "#0a111c",
              cursor: "zoom-in"
            }}
            onClick={() => openZoom(src, alt)}
          />
        ) : (
          <div style={{ color: "var(--text-faint)" }}>Run Analyze to generate this image.</div>
        )}
      </div>
    </div>
  );

  const renderProgressRow = (label: string, value: number, total: number, accent: string) => {
    const percent = total ? clampPercent((value / total) * 100) : 0;
    return (
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: 12,
            color: "var(--text-muted)",
            marginBottom: 4
          }}
        >
          <span>{label}</span>
          <span>{total ? `${value}/${total}` : "-"}</span>
        </div>
        <div
          style={{
            height: 8,
            borderRadius: 999,
            background: "rgba(255, 255, 255, 0.08)",
            overflow: "hidden",
            border: "1px solid #2a3f5f"
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${percent}%`,
              background: accent,
              transition: "width 0.2s ease"
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div
      className="grid"
      style={{
        gap: 12,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        overflowX: "clip"
      }}
    >
      <div style={{ display: "grid", gap: 10 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap"
          }}
        >
          <div className="label" style={{ maxWidth: 760 }}>
            {isShowMode
              ? "Choose a curated thickness preset to load the published analysis."
              : "Select bottles in the table, then click Analyze to average thickness values (grouped by slice, height, and degree) and render combined plots via thickness_plotter."}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {!isShowMode && (
              <button
                className="button"
                onClick={handleAnalyze}
                disabled={analysisBusy || selectedIds.size === 0}
              >
                {analysisPreflighting
                  ? "Checking selected bottles..."
                  : analysisRunning
                  ? `Analyzing…${runningSeconds ? ` · ${runningSeconds}s` : ""}`
                  : selectedIds.size === 1
                  ? "Show Bottle"
                  : analysisDurationMs != null
                  ? `Analyze (Last ${(analysisDurationMs / 1000).toFixed(1)}s)`
                  : "Analyze"}
              </button>
            )}
            <button className="button secondary" onClick={handleReset}>
              Reset View
            </button>
            {!isShowMode && (
              <button
                className="button secondary"
                onClick={handleOpenSync}
                disabled={syncRunning || needsLogin}
              >
                Sync NAS
              </button>
            )}
          </div>
        </div>
        {isShowMode && (
          <div className="card" style={{ background: "var(--surface-elevated)" }}>
            <div className="label" style={{ marginBottom: 8 }}>
              Thickness Preset
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <select
                className="select mobile-full"
                value={showSelectedAnalysisId ?? ""}
                onChange={(event) => {
                  const raw = Number(event.target.value);
                  setShowSelectedAnalysisId(Number.isFinite(raw) && raw > 0 ? raw : null);
                  setShowAnalysisStatus(null);
                  setAnalysisSaveError(null);
                  setAnalysisError(null);
                }}
                disabled={showAnalysisBusy || loading || !showThicknessAnalyses.length}
              >
                <option value="">
                  {showAnalysisLoading ? "Loading presets..." : "Select preset"}
                </option>
                {showThicknessAnalyses.map((analysis) => (
                  <option key={analysis.id} value={analysis.id}>
                    #{analysis.id} {analysis.name} ({analysis.bottle_id_array.length} bottles)
                  </option>
                ))}
              </select>
              <button
                className="button"
                disabled={showAnalysisBusy || loading || showSelectedAnalysisId == null}
                onClick={() => {
                  void handleLoadShowAnalysis(showSelectedAnalysisId);
                }}
              >
                {showAnalysisHydrating ? "Loading..." : "Show Preset"}
              </button>
              <button
                className="button secondary"
                disabled={showAnalysisLoading}
                onClick={() => {
                  void loadShowThicknessAnalyses();
                }}
              >
                {showAnalysisLoading ? "Refreshing..." : "Refresh Presets"}
              </button>
            </div>
            {showAnalysisStatus && (
              <div className="status-error" style={{ marginTop: 8 }}>
                {showAnalysisStatus}
              </div>
            )}
          </div>
        )}
        {analysisResult && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <span className="pill">
              Output: {analysisResult.dir}/
              {analysisResult.stub}
            </span>
          </div>
        )}
        {analysisError && (
          <div className="status-error" style={{ marginTop: 2 }}>
            {analysisError}
          </div>
        )}
        {analysisWarning && (
          <div className="status-warning" style={{ marginTop: 2 }}>
            {analysisWarning}
          </div>
        )}
        {analysisStatusMessage && (
          <div className="status-ok" style={{ marginTop: 2 }}>
            {analysisStatusMessage}
          </div>
        )}
      </div>
      {isShowMode && showLoadedAnalysisId != null && (
        <ThicknessFilterPanel
          filters={draftFilters}
          onChange={setDraftFilters}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
          trialOptions={showTrialOptions}
          readOnly
          readOnlyValues={showReadOnlyFilterLabels}
        />
      )}
      {!isShowMode && (
        <>
          <ThicknessFilterPanel
            filters={draftFilters}
            onChange={setDraftFilters}
            onApply={handleApplyFilters}
            onClear={handleClearFilters}
            trialOptions={trialOptions}
          />

          <ThicknessTable
            data={filtered}
            selectedIds={selectedIds}
            onToggle={handleSelectToggle}
            onSelectAll={handleSelectAll}
            page={page}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
            sort={sort}
            onSortChange={setSort}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
          />
        </>
      )}

      {showHasLoadedAnalysis && (analysisBusy || analysisResult || analysisPayload || analysisBundleHydrating) && (
        <div
          className="card"
          style={{
            background: "var(--surface-elevated)",
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            boxSizing: "border-box",
            overflowX: "clip"
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
              width: "100%",
              maxWidth: "100%",
              minWidth: 0
            }}
          >
            <h3 className="section-title" style={{ margin: 0 }}>
              Combined Thickness Output
            </h3>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", minWidth: 0 }}>
              {!isShowMode && (
                <button
                  className="button secondary"
                  onClick={handleExportAnalysisBundle}
                  disabled={
                    analysisExportBusy ||
                    analysisBusy ||
                    (!analysisResult && !analysisPayload && !analysisContract)
                  }
                  title="Export HTML, image, metadata, and CSV files as a ZIP bundle"
                >
                  {analysisExportBusy ? "Exporting..." : "Export Bundle"}
                </button>
              )}
              <span className="pill" style={{ maxWidth: "100%", minWidth: 0 }}>
                <span
                  style={{
                    display: "inline-block",
                    maxWidth: "min(520px, 100%)",
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    verticalAlign: "bottom"
                  }}
                  title={
                    analysisResult
                      ? `${analysisResult.dir}/${analysisResult.stub}`
                      : analysisPayload
                      ? "Loaded from database"
                      : "Preparing..."
                  }
                >
                  {analysisResult
                    ? `${analysisResult.dir}/${analysisResult.stub}`
                    : analysisPayload
                    ? "Loaded from database"
                    : "Preparing..."}
                </span>
              </span>
            </div>
          </div>
          {analysisExportError && (
            <div className="status-error" style={{ marginTop: 8 }}>
              {analysisExportError}
            </div>
          )}
          {analysisBundleHydrating ? (
            <div
              style={{
                marginTop: 10,
                border: "1px dashed #2a3f5f",
                borderRadius: 14,
                padding: 18,
                color: "var(--text-muted)"
              }}
            >
              Loading modern plot bundle…
            </div>
          ) : analysisHasPlotBundle && analysisContract ? (
            <div style={{ marginTop: 10, width: "100%", maxWidth: "100%", minWidth: 0, overflowX: "clip" }}>
              <PlotBundlePanel analysis={analysisContract} />
            </div>
          ) : (
            <>
              <div style={{ marginTop: 10 }}>
                <div
                  className="plot-frame"
                  style={{
                    border: "1px solid #1e2b3d",
                    borderRadius: 14,
                    background: "var(--surface-elevated)",
                    padding: 8,
                    maxWidth: 1200,
                    margin: "0 auto",
                    display: "grid",
                    placeItems: "stretch"
                  }}
                >
                  {analysisPaths.iframe ? (
                    <iframe
                      src={analysisPaths.iframe}
                      title="Thickness analysis"
                      style={{
                        width: "100%",
                        height: "100%",
                        border: "none",
                        borderRadius: 10,
                        background: "var(--surface-elevated)"
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        height: "100%",
                        display: "grid",
                        placeItems: "center",
                        color: "var(--text-faint)",
                        border: "1px dashed #2a3f5f",
                        borderRadius: 10
                      }}
                    >
                      {analysisStatusMessage ||
                        (analysisRunning
                          ? "Averaging thickness data and rendering plots…"
                          : "Run Analyze to load the combined thickness report.")}
                    </div>
                  )}
                </div>
              </div>
              <div className="responsive-grid" style={{ marginTop: 12 }}>
                {renderImageCard(
                  "Heatmap",
                  analysisPaths.heatmap,
                  "Combined thickness heatmap",
                  "var(--accent-two)"
                )}
                {renderImageCard(
                  "Slices profile (relative)",
                  analysisPaths.slicesRelative,
                  "Combined relative slices profile",
                  "var(--accent-one)"
                )}
                {renderImageCard(
                  "Slices profile",
                  analysisPaths.slicesAbsolute,
                  "Combined slices profile",
                  "var(--accent-three)"
                )}
              </div>
            </>
          )}
        </div>
      )}

      {!isShowMode && (
        <div className="card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <h3 className="section-title" style={{ margin: 0 }}>
            Save / Load Multiple Analysis
          </h3>
          <span className="pill">Selected bottles: {selectedIds.size || "none"}</span>
        </div>
        <div style={{ marginTop: 8, display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 240px" }}>
            <div className="label">Analysis Name</div>
            <input
              className="input"
              placeholder="e.g. Line change 12/02"
              value={analysisName}
              onChange={(event) => {
                setAnalysisName(event.target.value);
                if (analysisSaveError) setAnalysisSaveError(null);
              }}
              disabled={analysisSaveBusy}
            />
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
            <button className="button" disabled={!canSaveAnalysis} onClick={handleSaveAnalysis}>
              Save Analysis
            </button>
            {analysisSaveBusy && <span className="label">Saving…</span>}
          </div>
        </div>
        {analysisSaveError && <div className="status-error">{analysisSaveError}</div>}
        {analysisPayload && analysisBottleIds.length > 0 && !analysisMatchesSelection && (
          <div className="label" style={{ color: "#f7d774", marginTop: 6 }}>
            Selection changed since the last analysis. Run Analyze again before saving.
          </div>
        )}
        {analysisResult &&
          analysisPayload &&
          mountPoint &&
          analysisResult.dir.startsWith(mountPoint) && (
          <div className="label" style={{ color: "var(--text-muted)", marginTop: 6 }}>
            Analysis outputs are already stored on the NAS. Run Analyze again to save a new copy.
          </div>
        )}

        <div className="divider" />

        {savedAnalyses.length ? (
          <div className="table-scroll">
            <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Bottles</th>
                <th>Saved At</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {savedAnalyses.map((analysis) => (
                <tr key={analysis.id}>
                  <td>{analysis.name}</td>
                  <td className="text-wrap-anywhere" style={{ maxWidth: "min(320px, 60vw)" }}>
                    {analysis.bottle_id_array.join(", ")}
                  </td>
                  <td>{formatAppDateTime(analysis.timestamp, "-")}</td>
                  <td style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <button className="button secondary" onClick={() => handleRestoreAnalysis(analysis)}>
                      Load
                    </button>
                    <button
                      className="button secondary"
                      onClick={() => setPendingDelete(analysis)}
                      disabled={analysisSaveBusy}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </div>
        ) : (
          <div style={{ color: "var(--text-faint)" }}>No analyses saved yet.</div>
        )}
      </div>
      )}

      {showHasLoadedAnalysis && analysisTriggered ? (
        <ThicknessGallery
          bottles={selectedBottles}
          nasMountPath={mountPoint}
          cacheBustKey={galleryCacheBustKey}
          onUnselect={(id) => handleSelectToggle(id)}
          onViewDetail={handleViewDetail}
          page={galleryPage}
          pageSize={8}
          onPageChange={setGalleryPage}
        />
      ) : (
        <div className="card" style={{ textAlign: "center", color: "var(--text-faint)" }}>
          {isShowMode
            ? "Select a thickness preset, then click Show Preset to load the curated analysis."
            : "Run Analyze to load gallery items for the selected bottles."}
        </div>
      )}

      {loading && <div className="card">Loading thickness bottles from PostgREST…</div>}
      {loadError && (
        <div className="card" style={{ border: "1px solid #ff9f9f" }}>
          <div className="status-error">Metadata load error: {loadError}</div>
        </div>
      )}
      {nasError && (
        <div className="card" style={{ border: "1px solid #ff9f9f" }}>
          <div className="status-error">NAS error: {nasError}</div>
        </div>
      )}

      {isShowMode && showAnalysisHydrating && (
        <div className="show-loading-overlay">
          <div className="loading-popup" role="status" aria-live="polite">
            <div className="spinner" aria-label="Loading thickness preset" />
            <div>
              <div className="section-title" style={{ marginBottom: 4 }}>
                Loading preset…
              </div>
              <div className="label">Preparing the published thickness analysis.</div>
            </div>
          </div>
        </div>
      )}

      <NasLoginModal
        open={needsLogin}
        credentials={nasCreds}
        onChange={(cfg) => {
          setNasCreds(cfg);
          setConfig(cfg);
        }}
        onSubmit={() => triggerMount(nasCreds)}
        status={nasStatus}
        error={nasError}
      />

      {!isShowMode && syncOpen && (
        <div className="modal-backdrop" style={{ zIndex: 2400 }}>
          <div className="modal" style={{ maxWidth: 900 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <h3 className="section-title" style={{ margin: 0 }}>
                Thickness Sync
              </h3>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                {syncPaused && <span className="pill">Paused</span>}
                {syncStopRequested && <span className="pill">Stopping...</span>}
                <span className="pill">NAS: {mountPoint || "not mounted"}</span>
              </div>
            </div>
            <div className="label" style={{ marginTop: 8 }}>
              Scans NAS trial folders, processes new bottles, and inserts results into the database.
            </div>
            {syncPresetIds.length > 0 && (
              <div
                className="card"
                style={{
                  background: "var(--surface-elevated)",
                  borderColor: "var(--accent-one)",
                  marginTop: 12
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                  <div>
                    <div className="label">Recalc preset</div>
                    <div style={{ marginTop: 4, color: "var(--text-primary)", fontWeight: 700 }}>
                      {syncPresetIds.length} outdated bottle(s), selected-only recalc
                    </div>
                  </div>
                  <span className="pill">Live DB update</span>
                </div>
                <div
                  style={{
                    marginTop: 8,
                    maxHeight: 74,
                    overflow: "auto",
                    display: "flex",
                    gap: 6,
                    flexWrap: "wrap"
                  }}
                >
                  {syncPresetIds.map((id) => (
                    <span key={id} className="pill">
                      {id}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginTop: 10,
                fontSize: 13,
                color: "var(--text-muted)"
              }}
            >
              <input
                type="checkbox"
                checked={syncDryRun}
                onChange={(event) => setSyncDryRun(event.target.checked)}
                disabled={syncRunning}
              />
              Dry run (no DB writes)
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginTop: 8,
                fontSize: 13,
                color: "var(--text-muted)"
              }}
            >
              <input
                type="checkbox"
                checked={syncRecalcExisting}
                onChange={(event) => {
                  const enabled = event.target.checked;
                  setSyncRecalcExisting(enabled);
                  if (!enabled) {
                    setSyncRecalcSelectedOnly(false);
                    setSyncSelectedBottleOverrideIds(null);
                  }
                }}
                disabled={syncRunning}
              />
              Recalc existing bottles (recompute raw data and overwrite DB rows)
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginTop: 6,
                fontSize: 13,
                color: syncRecalcExisting ? "var(--text-muted)" : "var(--text-faint)"
              }}
            >
              <input
                type="checkbox"
                checked={syncRecalcSelectedOnly}
                onChange={(event) => {
                  setSyncRecalcSelectedOnly(event.target.checked);
                  if (!event.target.checked) {
                    setSyncSelectedBottleOverrideIds(null);
                  }
                }}
                disabled={syncRunning || !syncRecalcExisting}
              />
              Recalc only currently selected bottles in table
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginTop: 6,
                fontSize: 13,
                color: "var(--text-muted)"
              }}
            >
              <input
                type="checkbox"
                checked={syncRecalcSavedAnalyses}
                onChange={(event) => setSyncRecalcSavedAnalyses(event.target.checked)}
                disabled={syncRunning}
              />
              Recalc saved multiple analyses (update existing saved rows)
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginTop: 6,
                fontSize: 13,
                color: syncRecalcSavedAnalyses ? "var(--text-muted)" : "var(--text-faint)"
              }}
            >
              <input
                type="checkbox"
                checked={syncRecalcSavedRequireSingles}
                onChange={(event) => setSyncRecalcSavedRequireSingles(event.target.checked)}
                disabled={syncRunning || !syncRecalcSavedAnalyses}
              />
              Require all member single bottles to be up-to-date before saved-analysis recalc
            </label>

            {(syncRunning || syncProgress.foldersTotal > 0) && (
              <div className="card" style={{ background: "var(--surface-elevated)", marginTop: 12 }}>
                <div className="label" style={{ marginBottom: 8 }}>
                  Sync Progress
                </div>
                <div style={{ display: "grid", gap: 10 }}>
                  {renderProgressRow(
                    "Folders",
                    syncProgress.foldersDone,
                    syncProgress.foldersTotal,
                    "var(--accent-one)"
                  )}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: 12,
                      color: "var(--text-muted)"
                    }}
                  >
                    <span>Current folder</span>
                    <span style={{ color: "var(--text-primary)" }}>
                      {syncProgress.currentFolder || "-"}
                    </span>
                  </div>
                  {renderProgressRow(
                    "Bottles in folder",
                    syncProgress.filesDone,
                    syncProgress.filesTotal,
                    "var(--accent-two)"
                  )}
                  {renderProgressRow(
                    "Total bottles",
                    syncProgress.totalFilesDone,
                    syncProgress.totalFiles,
                    "var(--accent-three)"
                  )}
                </div>
              </div>
            )}

            <div
              className="card"
              style={{
                background: "var(--surface-elevated)",
                marginTop: 12,
                maxHeight: 280,
                overflow: "auto",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace",
                fontSize: 12
              }}
            >
              {syncLogs.length ? (
                <div style={{ display: "grid", gap: 4 }}>
                  {syncLogs.map((entry, index) => (
                    <div
                      key={`${index}-${entry.message}`}
                      style={{
                        color: LOG_TONE_COLORS[entry.tone],
                        whiteSpace: "pre-wrap"
                      }}
                    >
                      {entry.message}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: "var(--text-faint)" }}>No logs yet.</div>
              )}
            </div>

            {syncError && (
              <div className="status-error" style={{ marginTop: 10 }}>
                {syncError}
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
              <button
                className="button secondary"
                onClick={() => setSyncLogs([])}
                disabled={syncRunning}
              >
                Clear Logs
              </button>
              <button
                className="button secondary"
                onClick={handleToggleSyncPause}
                disabled={!syncRunning}
              >
                {syncPaused ? "Resume" : "Pause"}
              </button>
              <button
                className="button secondary"
                onClick={handleStopSync}
                disabled={!syncRunning || syncStopRequested}
              >
                {syncStopRequested ? "Stopping..." : "Stop Sync"}
              </button>
              <button
                className="button secondary"
                onClick={handleClearDryRunOutputs}
                disabled={syncRunning}
              >
                Clear Dry Run Outputs
              </button>
              <button className="button secondary" onClick={handleCloseSync} disabled={syncRunning}>
                Close
              </button>
              <button
                className="button"
                onClick={handleRequestStartSync}
                disabled={syncRunning || needsLogin}
              >
                {syncRunning ? "Syncing..." : "Start Sync"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={pendingRecalcSyncStart}
        title="Start Recalc Sync?"
        message={
          syncRecalcExisting && syncRecalcSavedAnalyses
            ? syncRecalcSelectedOnly
              ? "This will recompute raw thickness data for currently selected bottles, then recalculate saved multiple analyses in-place. Continue?"
              : "This will recompute raw thickness data for existing bottles and recalculate saved multiple analyses in-place. Continue?"
            : syncRecalcExisting
              ? syncRecalcSelectedOnly
                ? "This will recompute raw thickness data for the currently selected bottles and overwrite their stored analysis fields (including mean_thickness_profile and analysis_payload). Continue?"
                : "This will recompute raw thickness data for existing bottles and overwrite their stored analysis fields (including mean_thickness_profile and analysis_payload). Continue?"
              : "This will recalculate saved multiple analyses in-place and overwrite their stored analysis fields. Continue?"
        }
        confirmLabel="Start Recalc"
        onConfirm={() => {
          setPendingRecalcSyncStart(false);
          void handleStartSync();
        }}
        onCancel={() => setPendingRecalcSyncStart(false)}
        busy={syncRunning}
      />

      {schemaRecalcPrompt && (
        <div className="modal-backdrop" style={{ zIndex: 2600 }}>
          <div className="modal" style={{ maxWidth: 640 }}>
            <h3 className="section-title" style={{ marginTop: 0 }}>
              {schemaRecalcPrompt.title || "Outdated Analysis Data"}
            </h3>
            <div className="label" style={{ marginTop: 6 }}>
              Some selected bottles need recalc before they can be used in a current thickness analysis.
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: 8,
                marginTop: 14
              }}
            >
              <span className="pill">Needs action: {schemaRecalcPrompt.bottleIds.length}</span>
              <span className="pill">Selected: {schemaRecalcPrompt.totalSelected ?? selectedIds.size}</span>
              <span className="pill">Expected: {THICKNESS_ANALYSIS_SCHEMA_VERSION}</span>
              <span className="pill">Current: {schemaPromptRemainingCount}</span>
              {schemaPromptFailedIds.length > 0 && (
                <span className="pill">Failed Recalc: {schemaPromptFailedIds.length}</span>
              )}
            </div>

            <div style={{ marginTop: 14 }}>
              <div className="label">Detected schema(s)</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                {(schemaPromptDetectedSchemas.length ? schemaPromptDetectedSchemas : ["missing"]).map(
                  (schema) => (
                    <span key={schema} className="pill">
                      {schema}
                    </span>
                  )
                )}
              </div>
            </div>

            {schemaPromptFailedIds.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <div className="label">Failed Recalc</div>
                <div
                  style={{
                    display: "grid",
                    gap: 6,
                    marginTop: 6,
                    maxHeight: 108,
                    overflow: "auto"
                  }}
                >
                  {schemaPromptFailedIds.map((id) => {
                    const status = schemaRecalcPrompt.recalcStatusById?.[id];
                    return (
                      <div
                        key={id}
                        className="status-warning"
                        style={{ padding: "7px 9px", margin: 0 }}
                      >
                        <strong>Bottle {id}</strong>
                        {status?.error_message ? `: ${status.error_message}` : ""}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{ marginTop: 14 }}>
              <div className="label">Samples needing action</div>
              <div
                style={{
                  marginTop: 6,
                  maxHeight: 240,
                  overflow: "auto",
                  border: "1px solid #263b59",
                  borderRadius: 10,
                  display: "grid",
                  gap: 0,
                  background: "rgba(255, 255, 255, 0.03)"
                }}
              >
                {schemaRecalcPrompt.bottleIds.map((id) => (
                  <div
                    key={id}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "minmax(70px, 1fr) auto auto",
                      alignItems: "center",
                      gap: 12,
                      padding: "9px 10px",
                      borderBottom: "1px solid rgba(255,255,255,0.07)"
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: "var(--text-primary)", fontWeight: 700 }}>
                        Bottle {id}
                        {schemaPromptFailedSet.has(id) ? " · Failed Recalc" : ""}
                      </div>
                      <div className="label" style={{ marginTop: 2 }}>
                        {schemaPromptFailedSet.has(id)
                          ? schemaRecalcPrompt.recalcStatusById?.[id]?.error_message || "Last recalc failed"
                          : schemaRecalcPrompt.schemaById?.[id] ?? "missing"}
                      </div>
                    </div>
                    <label
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        color: "var(--text-muted)",
                        fontSize: 13
                      }}
                    >
                      <input
                        type="radio"
                        name={`schema-action-${id}`}
                        checked={(schemaRecalcChoices[id] ?? "recalc") === "recalc"}
                        onChange={() => handleSetSchemaRecalcChoice(id, "recalc")}
                      />
                      Recalc
                    </label>
                    <label
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        color: "var(--text-muted)",
                        fontSize: 13
                      }}
                    >
                      <input
                        type="radio"
                        name={`schema-action-${id}`}
                        checked={(schemaRecalcChoices[id] ?? "recalc") === "exclude"}
                        onChange={() => handleSetSchemaRecalcChoice(id, "exclude")}
                      />
                      Exclude
                    </label>
                  </div>
                ))}
              </div>
              <div className="label" style={{ marginTop: 8 }}>
                Recalc selected: {schemaPromptRecalcIds.length} · Exclude selected: {schemaPromptExcludeIds.length}
              </div>
            </div>

            {!schemaPromptRemainingCount && (
              <div className="status-warning" style={{ marginTop: 12 }}>
                All selected bottles are failed or outdated, so there are no current bottles to analyze.
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18, flexWrap: "wrap" }}>
              <button type="button" className="button secondary" onClick={() => setPendingRecalcPrompt(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="button secondary"
                onClick={() => void handleExcludeOutdatedAndAnalyzeRemaining()}
                disabled={!schemaPromptRemainingCount || analysisBusy}
              >
                Analyze Current Only ({schemaPromptRemainingCount})
              </button>
              <button
                type="button"
                className="button"
                onClick={handleOpenRecalcFromPrompt}
                disabled={syncRunning || schemaPromptRecalcIds.length === 0}
              >
                {syncRunning ? "Sync Running..." : `Open Recalc (${schemaPromptRecalcIds.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!rawDataRecalcPrompt}
        title={rawDataRecalcPrompt?.title || "Recalc Recommended"}
        message={
          rawDataRecalcPrompt?.message ||
          "The selected bottles are missing required thickness data. Open recalc with selected-bottles preset?"
        }
        confirmLabel="Open Recalc"
        onConfirm={handleOpenRecalcFromPrompt}
        onCancel={() => {
          setPendingRecalcPrompt(null);
        }}
        busy={syncRunning}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete multiple analysis?"
        message={
          pendingDelete
            ? `Are you sure you want to delete \"${pendingDelete.name}\"? This will remove the saved multiple analysis and cannot be undone.`
            : "Are you sure you want to delete this multiple analysis? This cannot be undone."
        }
        confirmLabel="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
        busy={analysisSaveBusy}
      />

      <ThicknessDetailModal
        open={!!detailBottle}
        bottle={detailBottle}
        hydrating={detailBottle ? detailBundleHydratingIds.has(detailBottle.id) : false}
        nasMountPath={mountPoint}
        onClose={() => setDetailBottle(null)}
      />

      {analysisPreflighting && (
        <div className="modal-backdrop" style={{ zIndex: 2500 }}>
          <div className="loading-popup" style={{ minWidth: 360, alignItems: "stretch" }}>
            <div className="spinner" aria-label="Checking selected bottles" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="section-title" style={{ marginBottom: 4 }}>
                Checking selected bottles…
              </div>
              <div className="label">
                {analysisProgress?.label || analysisStatusMessage || "Preparing analysis"}
              </div>
              {analysisProgress?.detail && (
                <div className="label" style={{ marginTop: 2, color: "var(--text-faint)" }}>
                  {analysisProgress.detail}
                </div>
              )}
              <div
                style={{
                  height: 8,
                  borderRadius: 999,
                  background: "rgba(255, 255, 255, 0.08)",
                  overflow: "hidden",
                  border: "1px solid #2a3f5f",
                  marginTop: 12
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${analysisProgress?.percent ?? 8}%`,
                    background: "var(--accent-one)",
                    transition: "width 0.2s ease"
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {analysisRunning && (
        <div className="modal-backdrop" style={{ zIndex: 2500 }}>
          <div className="loading-popup">
            <div className="spinner" aria-label="Running thickness analysis" />
            <div>
              <div className="section-title" style={{ marginBottom: 4 }}>
                Running thickness analysis…
              </div>
              <div className="label">
                Averaging selected bottles and rendering plots
                {runningSeconds ? ` · ${runningSeconds}s` : ""}
              </div>
            </div>
          </div>
        </div>
      )}

      {zoomSrc && (
        <div
          className="modal-backdrop"
          style={{ zIndex: 2600, cursor: "zoom-out" }}
          onClick={() => setZoomSrc(null)}
        >
          <div
            className="modal"
            style={{
              width: "90vw",
              maxWidth: 1400,
              height: "90vh",
              display: "grid",
              placeItems: "center",
              background: "var(--app-bg-base)",
              cursor: "zoom-out"
            }}
            onClick={() => setZoomSrc(null)}
          >
            <img
              src={zoomSrc}
              alt={zoomAlt || "Zoomed output"}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                borderRadius: 14,
                border: "1px solid #30486a",
                boxShadow: "0 20px 45px rgba(0,0,0,0.55)",
                cursor: "zoom-out"
              }}
            />
            <button
              className="button secondary"
              style={{ position: "absolute", top: 16, right: 16 }}
              onClick={() => setZoomSrc(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

type AddZipEntry = (path: string, data: string | Uint8Array) => void;

function buildZipEntryAdder(entries: ZipEntryInput[], warnings: string[]): AddZipEntry {
  const seen = new Set<string>();
  return (path, data) => {
    const normalized = path.replace(/\\/g, "/").replace(/^\/+/, "");
    if (!normalized || seen.has(normalized)) {
      warnings.push(`Skipped duplicate or invalid export entry: ${path}`);
      return;
    }
    seen.add(normalized);
    entries.push({ path: normalized, data });
  };
}

async function addThicknessOutputFiles(
  addEntry: AddZipEntry,
  warnings: string[],
  result: ThicknessAnalysisResult
) {
  if (!window.api?.readTextFile || !window.api?.readBase64File) {
    warnings.push("Desktop file reader is unavailable; output files were not bundled.");
    return;
  }

  const cleanedDir = result.dir.replace(/\\/g, "/").replace(/\/$/, "");
  const outputStub = result.stub || THICKNESS_ANALYSIS_STUB;
  const metadataRoot = deriveTmpRoot(cleanedDir);
  const textFiles = [
    {
      diskPath: `${cleanedDir}/${outputStub}_thickness_plots.html`,
      zipPath: `outputs/${outputStub}_thickness_plots.html`
    },
    {
      diskPath: `${metadataRoot}/${outputStub}.json`,
      zipPath: `metadata/${outputStub}.json`
    },
    {
      diskPath: `${metadataRoot}/${outputStub}_payload.json`,
      zipPath: `metadata/${outputStub}_payload.json`
    }
  ];
  const binaryFiles = THICKNESS_OUTPUT_SUFFIXES
    .filter((suffix) => suffix.toLowerCase().endsWith(".png"))
    .map((suffix) => ({
      diskPath: `${cleanedDir}/${outputStub}${suffix}`,
      zipPath: `outputs/${outputStub}${suffix}`
    }));

  for (const file of textFiles) {
    const response = await window.api.readTextFile(file.diskPath);
    if (response.ok && typeof response.text === "string") {
      addEntry(file.zipPath, response.text);
    } else {
      warnings.push(`Missing export file: ${file.diskPath} (${response.message || "read failed"})`);
    }
  }

  for (const file of binaryFiles) {
    const response = await window.api.readBase64File(file.diskPath);
    if (response.ok && typeof response.base64 === "string") {
      addEntry(file.zipPath, base64ToBytes(response.base64));
    } else {
      warnings.push(`Missing export file: ${file.diskPath} (${response.message || "read failed"})`);
    }
  }
}

function base64ToBytes(base64: string) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function formatJson(value: unknown) {
  return `${JSON.stringify(value, null, 2)}\n`;
}

function recordsToCsv(rows: Array<Record<string, unknown>>, preferredColumns: string[] = []) {
  const columns = Array.from(
    new Set([
      ...preferredColumns,
      ...rows.flatMap((row) => Object.keys(row)).sort()
    ])
  );
  if (!columns.length) return "";
  const lines = [
    columns.map(csvCell).join(","),
    ...rows.map((row) => columns.map((column) => csvCell(row[column])).join(","))
  ];
  return `${lines.join("\n")}\n`;
}

function csvCell(value: unknown) {
  if (value == null) return "";
  const text =
    typeof value === "object"
      ? JSON.stringify(value)
      : typeof value === "number" && Number.isFinite(value)
      ? String(value)
      : String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function buildThicknessBundleFileName(bottleIds: number[]) {
  const stamp = new Date().toISOString().replace(/[:.]/g, "-");
  const idSegment = bottleIds.length
    ? `${bottleIds.length}_bottles_${bottleIds.slice(0, 6).join("_")}${bottleIds.length > 6 ? "_more" : ""}`
    : "analysis";
  return `combined_thickness_output_${idSegment}_${stamp}`;
}

function averageThicknessData(dataMap: Record<number, ThicknessDataPoint[]>): ThicknessDataPoint[] {
  const aggregates = new Map<
    string,
    { slice: number; height: number; degree: number; sum: number; count: number; index?: number }
  >();
  Object.values(dataMap).forEach((rows) => {
    (rows || []).forEach((row) => {
      const slice = Number(row.slice);
      const height = Number(row.height);
      const degree = Number(row.degree);
      const thickness = Number(row.thickness);
      if (![slice, height, degree, thickness].every((v) => Number.isFinite(v))) return;
      const key = `${slice}|${height}|${degree}`;
      const current =
        aggregates.get(key) || { slice, height, degree, sum: 0, count: 0, index: row.index };
      current.sum += thickness;
      current.count += 1;
      if (current.index == null && row.index != null) {
        current.index = row.index;
      }
      aggregates.set(key, current);
    });
  });
  return Array.from(aggregates.values())
    .map((entry) => ({
      slice: entry.slice,
      height: entry.height,
      degree: entry.degree,
      thickness: entry.count ? entry.sum / entry.count : 0,
      index: entry.index
    }))
    .sort((a, b) => a.slice - b.slice || a.height - b.height || a.degree - b.degree);
}

function canonicalizeMeanProfile(points: unknown): ThicknessMeanProfilePoint[] {
  if (!Array.isArray(points)) return [];
  const grouped = new Map<number, { sum: number; count: number }>();
  points.forEach((entry) => {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) return;
    const rec = entry as Record<string, unknown>;
    const height = Number(rec.height);
    const thickness = Number(rec.thickness);
    if (!Number.isFinite(height) || !Number.isFinite(thickness)) return;
    const current = grouped.get(height) ?? { sum: 0, count: 0 };
    current.sum += thickness;
    current.count += 1;
    grouped.set(height, current);
  });
  return Array.from(grouped.entries())
    .map(([height, value]) => ({
      height,
      thickness: value.count > 0 ? value.sum / value.count : 0
    }))
    .sort((a, b) => a.height - b.height);
}

function buildThicknessPayload(
  bottles: ThicknessBottle[],
  _sampleIds: number[],
  dataMap: Record<number, ThicknessDataPoint[]>
): ThicknessAnalysisPayload {
  const bottleProfiles = bottles.map((b) => b.bottle_profile).filter(Boolean) as string[];
  const numberOfSlices = bottles.find((b) => Number.isFinite(b.number_of_slices))?.number_of_slices;
  const relativeIds = bottles
    .map((bottle) => bottle.relative_bottle_id)
    .filter((id) => Number.isFinite(id)) as number[];
  const normalizationSourceRows = bottles
    .map((bottle) => ({
      bottle_id: bottle.id,
      data: Array.isArray(dataMap[bottle.id]) ? dataMap[bottle.id] : []
    }))
    .filter((entry) => entry.data.length > 0);
  const fallbackRows =
    normalizationSourceRows.length > 1
      ? normalizationSourceRows[0]?.data ?? []
      : averageThicknessData(dataMap);

  const ciSourceProfiles =
    bottles.length > 1
      ? bottles
          .map((bottle) => {
            const fromBottle = canonicalizeMeanProfile(bottle.mean_thickness_profile);
            const fromAnalysis = canonicalizeMeanProfile(bottle.analysis_payload?.mean_thickness_profile);
            const profile = fromBottle.length > 1 ? fromBottle : fromAnalysis.length > 1 ? fromAnalysis : [];
            if (profile.length <= 1) return null;
            return {
              bottle_id: bottle.id,
              mean_thickness_profile: profile
            };
          })
          .filter(
            (
              entry
            ): entry is { bottle_id: number; mean_thickness_profile: ThicknessMeanProfilePoint[] } => !!entry
          )
      : [];

  const payload: ThicknessAnalysisPayload = {
    relative_bottle_id: relativeIds.length === 1 ? relativeIds[0] : relativeIds,
    bottle_profile: bottleProfiles[0],
    number_of_slices: typeof numberOfSlices === "number" ? numberOfSlices : undefined,
    data: fallbackRows
  };

  if (ciSourceProfiles.length > 1 || normalizationSourceRows.length > 1) {
    payload.analysis_payload = {};
    if (ciSourceProfiles.length > 1) {
      payload.analysis_payload.ci_input = {
        measurement_ci_level: 0.95,
        source_profiles: ciSourceProfiles
      };
    }
    if (normalizationSourceRows.length > 1) {
      payload.analysis_payload.normalization_input = {
        mode: "per_slice_mean_spline_resample_v1",
        height_grid: "overlap",
        height_step_mm: 0.5,
        spline_bin_width_mm: 8.0,
        source_rows: normalizationSourceRows
      };
    }
  }

  return payload;
}

function buildAnalysisPaths(result: ThicknessAnalysisResult | null) {
  if (!result || !result.dir || !result.stub) {
    return { iframe: "", heatmap: "", slicesRelative: "", slicesAbsolute: "" };
  }
  const cleanedDir = result.dir.replace(/\\/g, "/").replace(/\/$/, "");
  const base = `${cleanedDir}/${result.stub}`;
  const cacheKey = Number.isFinite(result.runId) ? `?v=${result.runId}` : "";
  return {
    iframe: toAppFileUrl(`${base}_thickness_plots.html${cacheKey}`),
    heatmap: toAppFileUrl(`${base}_thickness_heatmap.png${cacheKey}`),
    slicesRelative: toAppFileUrl(`${base}_slices_profile_relative.png${cacheKey}`),
    slicesAbsolute: toAppFileUrl(`${base}_slices_profile.png${cacheKey}`)
  };
}

function buildSingleBottleOutputDir(mountPoint: string, bottle: ThicknessBottle) {
  const cleanedMount = mountPoint.replace(/\\/g, "/").replace(/\/$/, "");
  const trialName = bottle.trial_name || "unknown";
  return `${cleanedMount}/thickness_plotter/${trialName}/output`;
}

function buildSingleBottleStub(bottle: ThicknessBottle) {
  if (!Number.isFinite(bottle.relative_bottle_id)) return "";
  return `bottle${bottle.relative_bottle_id}`;
}
